/* eslint-disable no-empty */
import { MicHelp } from "../components/coach/MicHelp";
import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Loader2, Play, Pause, Plus, Star, Trash2, X, Upload as UploadIcon, Music2, Brain, Sparkles, ChevronRight, Mic, Check, RotateCcw, Share2, Search } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { trackAudioPlayed } from "../lib/dailyEssentialsTracker";
import AskAiJourneyModal from "./AskAiJourneyModal";
import { GiftIntro } from "../components/soulumoCreate/GiftIntro";
import SimpleCreateFlow from "../components/soulumoCreate/SimpleCreateFlow";
import { SoulumoOpening } from "../components/soulumoCreate/SoulumoOpening";
import { YearlyOfferCard } from "../components/soulumoCreate/YearlyOfferCard";
import { MembersOrDemo } from "../components/soulumoDemo/MembersOrDemo";
import ReadAloud from "../components/ReadAloud";
import InstallOrb from "../components/InstallOrb";
import SoulumoWord from "../components/SoulumoWord";
import SoulumoHeader from "../components/SoulumoHeader";
import SoulumoFooter from "../components/SoulumoFooter";
import FeedbackPromptHost from "../components/FeedbackPromptHost";
import useFeedbackPrompts from "../hooks/useFeedbackPrompts";
import { MoodJourneyChoices } from "../components/journey/MoodJourneyChoices";
import { NightStartButton, NightStartOverlay } from "../components/soulumoCreate/NightStart";
import { GolfWelcome } from "../components/soulumoCreate/GolfWelcome";

// Small helper: play the cinematic "arriving" state on mount, then
// settle into "resting" for the rest of the session.
function SoulumoHeroOrb() {
  const [state, setState] = React.useState("arriving");
  React.useEffect(() => {
    const t = setTimeout(() => setState("resting"), 2400);
    return () => clearTimeout(t);
  }, []);
  return <InstallOrb size={220} state={state} className="mb-3" />;
}

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem("reclaim_token");
const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

const TOK = {
  // Soulumo palette — sampled from Collective Alignment (Feb 2026,
  // Michael's request). Deep navy body, warm amber accents.
  ink: "#0d1f3a",
  cream: "#f5e0ad",
  creamDim: "rgba(245,224,173,0.75)",
  gold: "#e7c98a",
  goldSoft: "rgba(231,201,138,0.86)",
  goldMist: "rgba(231,201,138,0.24)",
  // Page background gradient (used for /my-journeys hero + wizard).
  pageBg: "radial-gradient(ellipse at 30% 20%, #1f3a5c 0%, #0d1f3a 35%, #060e1f 70%, #03060f 100%)",
};

/* ──────────────────────────────────────────────────────────── */
/*  My Journeys — list + create wizard + play                  */
/* ──────────────────────────────────────────────────────────── */
export default function MyJourneysPage() {
  const nav = useNavigate();
  // Mar 5 2026 · When served under a narrowed brand (soulumo.app),
  // we must not leak Reclaim-internal navigation (e.g. "← Private
  // Suite") back to the user — it breaks the standalone illusion.
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  // "Ask AI · Create My Journey" — natural-language intent → structured
  // journey via canonical library. Feb 2026 (Michael's request).
  const [askAiOpen, setAskAiOpen] = useState(false);
  const [explainStart, setExplainStart] = useState({ intent: searchParams.get("explain") || "", repeat: false });
  const [gift, setGift] = useState(null);
  const [giftIntro, setGiftIntro] = useState(searchParams.get("gift") === "1");
  useEffect(() => {
    if (!searchParams.get("explain")) return;
    setAskAiOpen(true);
    searchParams.delete("explain");
    setSearchParams(searchParams, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [prepPlan, setPrepPlan] = useState(null);
  const [prepFrom, setPrepFrom] = useState(null);
  useEffect(() => {
    // From the Soulumo guide: affirmations already agreed in "talk it through", so open Create at their words.
    if (!searchParams.get("guided")) return;
    const raw = sessionStorage.getItem("soulumo_guided_plan");
    sessionStorage.removeItem("soulumo_guided_plan");
    if (raw) { try { setPrepPlan(JSON.parse(raw)); setAskAiOpen(true); } catch { /* ignore */ } }
    searchParams.delete("guided");
    setSearchParams(searchParams, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    // "Make it in my voice" on a signature installation: open Create at its exact affirmations.
    const bpId = searchParams.get("mine");
    if (!bpId) return;
    const hdr = { headers: { Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` } };
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/audio-journeys/blueprints/${encodeURIComponent(bpId)}/as-plan`, hdr)
      .then((r) => { setPrepPlan(r.data); setAskAiOpen(true); }).catch(() => {});
    searchParams.delete("mine");
    setSearchParams(searchParams, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const id = searchParams.get("prep");
    if (!id) return;
    const paid = searchParams.get("paid");
    const hdr = { headers: { Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` } };
    const base = process.env.REACT_APP_BACKEND_URL;
    (async () => {
      // back from the coach-link checkout: confirm the payment before reopening their words
      for (let i = 0; paid && i < 6; i += 1) {
        const st = await axios.get(`${base}/api/voice-clone/checkout/status/${encodeURIComponent(paid)}`, hdr).catch(() => null);
        if (st?.data?.payment_status === "paid") break;
        await new Promise((res) => setTimeout(res, 2000));
      }
      const r = await axios.get(`${base}/api/soulumo/coach-prep/${encodeURIComponent(id)}`, hdr);
      setPrepFrom({ ...r.data, justPaid: !!paid }); setPrepPlan(r.data.plan); setAskAiOpen(true);
    })().catch(() => {});
    searchParams.delete("prep"); searchParams.delete("paid");
    setSearchParams(searchParams, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [advancedIntent, setAdvancedIntent] = useState("");
  // IDs created in the current tab session — shown in a distinct
  // "Just Created" band at the top so they don't get lost in a
  // long list. Cleared on tab reload.
  const [recentIds, setRecentIds] = useState(() => new Set());
  // Private "My Voice" library — every clip the user has ever recorded
  // for any journey, grouped by section. Opens as a calm full-screen
  // overlay; data NEVER leaves their account.
  const [voiceLibOpen, setVoiceLibOpen] = useState(false);
  // When the user picks a Signature Blueprint to use as a wizard
  // PRESET (vs one-tap clone), we hand the blueprint object to the
  // CreateWizard which seeds its form on mount. Cleared on close.
  const [blueprintPreset, setBlueprintPreset] = useState(null);

  // ── Beta Feedback & Intelligence · Feb 2026 ──────────────────
  // The orchestration hook listens for `soulumo:installation-ended`
  // events, waits 5s (emotional landing window), then asks the
  // server which prompt — if any — to show. `FeedbackPromptHost`
  // renders whichever card/modal the server returned.
  useFeedbackPrompts();

  // Deep-link support: /my-journeys?blueprint=<id> opens the wizard
  // pre-filled with that blueprint. Used by the Sleep Biohacking
  // dashboard to route users into the Day / Night sleep installations
  // without making them hunt through the Signature row.
  const deepLinkBlueprint = searchParams.get("blueprint");
  const [deepLinkHandled, setDeepLinkHandled] = useState(false);

  // Reload the journey list directly (not via useEffect closure) so the
  // call happens immediately from whatever caller, with no state-batching
  // or animation-cleanup race. Uses native fetch because axios.get was
  // observed to be silently aborted in some race conditions on the prod
  // Cloudflare ingress. Jun 9 2026.
  const reload = React.useCallback(async () => {
    try {
      const tok = getToken();
      const res = await fetch(`${API}/api/audio-journeys`, {
        headers: tok ? { Authorization: `Bearer ${tok}` } : {},
        cache: "no-store",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setItems(data?.items || []);
    } catch (e) {
      console.error("[journeys] list failed:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial mount fetch.
  useEffect(() => { reload(); }, [reload]);

  // Deep-link: open wizard pre-filled with the requested blueprint.
  // Fires once after first load, then clears the URL param so a
  // refresh doesn't re-trigger.
  //
  // Sleep blueprints (bp_sleep_day / bp_sleep_night) intentionally
  // BYPASS this wizard: they're fully curated and the Sleep
  // dashboard performs a one-tap clone directly. We still honour
  // the URL param for them (in case anyone bookmarks the link) by
  // one-tap-cloning instead of opening the wizard — preserves the
  // sleep context (no Performance theme picker).
  useEffect(() => {
    if (deepLinkHandled || !deepLinkBlueprint) return;
    let cancelled = false;
    (async () => {
      try {
        const tok = getToken();
        const r = await fetch(`${API}/api/audio-journeys/blueprints`, {
          headers: tok ? { Authorization: `Bearer ${tok}` } : {},
          cache: "no-store",
        });
        if (!r.ok) return;
        const data = await r.json();
        const bp = (data?.items || []).find((b) => b.id === deepLinkBlueprint);
        if (cancelled || !bp) return;
        const isSleep = bp.group === "Sleep" || /^bp_sleep_/.test(bp.id);
        if (isSleep) {
          // One-tap clone — keep the user in sleep context.
          try {
            await axios.post(`${API}/api/audio-journeys/blueprints/${bp.id}/clone`, {}, auth());
            await reload();
          } catch (e) { console.warn("[deep-link sleep clone] failed:", e); }
        } else {
          setBlueprintPreset(bp);
          setCreating(true);
        }
      } catch (e) {
        console.warn("[deep-link blueprint] fetch failed:", e);
      } finally {
        if (!cancelled) {
          setDeepLinkHandled(true);
          // Clear the query param so refresh doesn't loop.
          searchParams.delete("blueprint");
          setSearchParams(searchParams, { replace: true });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [deepLinkBlueprint, deepLinkHandled, searchParams, setSearchParams, reload]);

  // Re-poll while any journey is still rendering.
  useEffect(() => {
    const renderingIds = items.filter((j) => j.audio_status === "rendering").map((j) => j.id);
    if (!renderingIds.length) return;
    const t = setInterval(reload, 4000);
    return () => clearInterval(t);
  }, [items, reload]);

  // After the wizard submits, it hands us the new journey doc directly
  // from the POST 200 response. We prepend it to local state immediately
  // so the card shows up the moment the wizard closes — no race with
  // axios, animations, or React 18 strict-mode effect cleanup.
  const handleJourneyCreated = React.useCallback((newJourney) => {
    setCreating(false);
    if (newJourney && newJourney.id) {
      setItems((prev) => {
        // De-dupe by id in case a background poll also picked it up.
        if (prev.some((p) => p.id === newJourney.id)) return prev;
        return [newJourney, ...prev];
      });
      // Flag it as "just created" so it surfaces in its own band.
      setRecentIds((prev) => {
        const next = new Set(prev);
        next.add(newJourney.id);
        return next;
      });
    }
    // Schedule a real reload too so background-rendered audio_url shows up.
    setTimeout(reload, 800);
  }, [reload]);

  // Auto-open the installation when its render completes.
  // Mar 6 2026 · Michael's brief — after curation finishes, drop the
  // user straight into the installation player (Library page with the
  // just-finished row auto-expanded). Only fires if the user is still
  // on this page and hasn't navigated away.
  const prevStatusRef = React.useRef(new Map());
  useEffect(() => {
    const prev = prevStatusRef.current;
    for (const j of items) {
      const wasRendering = prev.get(j.id) === "rendering";
      if (wasRendering && j.audio_status === "ready") {
        try { window.sessionStorage.setItem("soulumo_ready_ping", j.id); } catch {}
        nav(`/my-library?open=${j.id}`);
        break;
      }
    }
    prevStatusRef.current = new Map(items.map((j) => [j.id, j.audio_status]));
  }, [items, nav]);

  // Compute an "unopened just-ready installation" id — powers the
  // blue notice above the wizard cards. Once the user visits its
  // player it is added to the localStorage seen-set and hidden.
  const [seenReadyIds, setSeenReadyIds] = useState(() => {
    try {
      const raw = window.localStorage.getItem("soulumo_seen_ready_ids") || "[]";
      return new Set(JSON.parse(raw));
    } catch { return new Set(); }
  });
  const readyToOpen = items.find(
    (j) => recentIds.has(j.id) && j.audio_status === "ready" && !seenReadyIds.has(j.id)
  );

  const dismissReadyNotice = React.useCallback((jid) => {
    setSeenReadyIds((prev) => {
      const next = new Set(prev);
      next.add(jid);
      try { window.localStorage.setItem("soulumo_seen_ready_ids", JSON.stringify([...next])); } catch {}
      return next;
    });
  }, []);

  return (
    <div className="min-h-screen relative overflow-hidden flex flex-col" style={{ background: TOK.pageBg, color: TOK.cream }}>
      {/* Soulumo home on every host (incl. preview) — never Reclaim chrome. */}
      <SoulumoHeader />
      {/* Faint distant orb — sits behind the hero as a signature
          watermark. Deliberately huge + blurred so it feels ambient
          rather than illustrative. */}
      <div
        className="pointer-events-none absolute -top-24 -right-24 opacity-40"
        style={{ filter: "blur(2px)" }}
        aria-hidden
      >
        <InstallOrb size={340} intensity={0.9} />
      </div>

      <div className="flex-1 max-w-3xl mx-auto px-5 sm:px-8 pt-6 pb-16 relative w-full">

        <MembersOrDemo>
        <YearlyOfferCard />
        <SoulumoOpening
          onBegin={(intent) => { setExplainStart({ intent, repeat: false }); setAskAiOpen(true); }}
          onRepeat={() => { setExplainStart({ intent: "", repeat: true }); setAskAiOpen(true); }}
          onResume={(draft) => { setExplainStart({ intent: "", repeat: false, draft }); setAskAiOpen(true); }}
          onBuildOwn={() => { setBlueprintPreset(null); setCreating(true); }} />
        </MembersOrDemo>

        {/* Ready-to-open notice — replaces the always-visible Library
            tile with a discrete cue that appears only when the user
            has a freshly-rendered installation they haven't opened
            yet. Mar 6 2026. Colour deliberately in Soulumo's
            twilight-blue accent so it feels distinct from the
            gold-toned installation cards below. */}
        {readyToOpen && (
          <button
            onClick={() => {
              dismissReadyNotice(readyToOpen.id);
              nav(`/my-library?open=${readyToOpen.id}`);
            }}
            data-testid="soulumo-installation-ready-notice"
            className="mt-8 w-full text-center rounded-xl px-5 py-3 transition-all"
            style={{
              background: "rgba(122,168,224,0.10)",
              border: "1px solid rgba(122,168,224,0.36)",
              color: "#a9c4ea",
              fontFamily: '"Manrope", sans-serif',
              fontSize: "0.72rem",
              letterSpacing: "0.24em",
              textTransform: "uppercase",
            }}
          >
            Your installation is ready <span style={{ marginLeft: 6 }}>→</span>
          </button>
        )}

        <div className="mb-6" />


        {/* Mar 6 2026 · The "Your Installations" list used to render
            here. Removed — it duplicated the dedicated /my-library
            page and cluttered the CHOOSE-and-CREATE surface. The
            prominent ★ YOUR LIBRARY card at the top of this page is
            the single, clean entry point to a user's own installs. */}
      </div>

      <GolfWelcome preview={searchParams.get("gym_welcome") === "1" ? "gym" : searchParams.get("golf_welcome") === "1"} onPick={(intent) => { setExplainStart({ intent, repeat: false }); setAskAiOpen(true); }} />
      <AnimatePresence>
        {giftIntro && <GiftIntro onClose={() => { setGiftIntro(false); searchParams.delete("gift"); setSearchParams(searchParams, { replace: true }); }}
          onStart={(g) => { setGift(g); setGiftIntro(false); searchParams.delete("gift"); setSearchParams(searchParams, { replace: true }); setExplainStart({ intent: "", repeat: false }); setAskAiOpen(true); }} />}
        {askAiOpen && (
          <SimpleCreateFlow
            gift={gift}
            initialIntent={explainStart.intent}
            startRepeat={explainStart.repeat}
            initialDraft={explainStart.draft || null}
            initialPlan={prepPlan}
            fromCoach={prepFrom}
            hidden={advancedOpen}
            onClose={() => { setAskAiOpen(false); setGift(null); setPrepPlan(null); setPrepFrom(null); setExplainStart({ intent: "", repeat: false }); }}
            onMoreOptionsBack={() => setAdvancedOpen(false)}
            onMoreOptions={(t) => { setAdvancedIntent(typeof t === "string" ? t : ""); setAdvancedOpen(true); }}
            onCreated={(j) => {
              setAskAiOpen(false);
              setGift(null);
              handleJourneyCreated(j);
            }}
          />
        )}
        {advancedOpen && (
          <AskAiJourneyModal
            initialIntent={advancedIntent}
            onClose={() => { setAdvancedOpen(false); setAskAiOpen(false); }}
            onBack={() => setAdvancedOpen(false)}
            onCreated={(j) => {
              setAdvancedOpen(false);
              setAskAiOpen(false);
              handleJourneyCreated(j);
            }}
          />
        )}
        {creating && (
          <CreateWizard
            onClose={() => { setCreating(false); setBlueprintPreset(null); }}
            onCreated={(j) => { setBlueprintPreset(null); handleJourneyCreated(j); }}
            blueprintPreset={blueprintPreset}
          />
        )}
        {voiceLibOpen && <VoiceLibrary onClose={() => setVoiceLibOpen(false)} />}
      </AnimatePresence>
      <SoulumoFooter />
      <FeedbackPromptHost />
    </div>
  );
}

/* ── Single journey card with play + favourite + delete ─────── */
// ── groupBySeries ────────────────────────────────────────────────
// Turn a flat list of journeys into a mixed list of solo journeys
// and series groups. Preserves original ordering by using the FIRST
// occurrence's index for the group's position. A "series" has all
// three days present; incomplete series (rare — one create failed)
// still render as a group so nothing is lost.
function groupBySeries(items) {
  const seen = new Set();
  const groups = new Map(); // group_id -> {day: journey}
  const order = [];
  for (const j of items) {
    if (j.series_group_id) {
      if (!groups.has(j.series_group_id)) {
        groups.set(j.series_group_id, {});
        order.push({ kind: "series", group_id: j.series_group_id });
      }
      groups.get(j.series_group_id)[j.series_day || 0] = j;
    } else {
      order.push({ kind: "solo", j });
    }
    seen.add(j.id);
  }
  return order.map((entry) => {
    if (entry.kind !== "series") return entry;
    const dayMap = groups.get(entry.group_id) || {};
    const days = Object.keys(dayMap)
      .map((n) => parseInt(n, 10))
      .sort((a, b) => a - b)
      .map((d) => dayMap[d]);
    return {
      kind: "series",
      group_id: entry.group_id,
      days,
      size: days[0]?.series_size || 3,
    };
  });
}

// ── SeriesSuperCard ──────────────────────────────────────────────
// One card that visually holds all three days of a build-up series.
// Collapsed: title + "Day X of Y", quick stats. Expanded: each day
// is a full JourneyCard so play / voice / actions work as expected.
function SeriesSuperCard({ entry, onChange }) {
  const [expanded, setExpanded] = useState(false);
  const days = entry.days || [];
  if (!days.length) return null;
  // The series display name = base of Day 3's name minus " · Playing Free" suffix.
  const anchor = days.find((d) => d?.series_day === 3) || days[days.length - 1];
  const baseName = (anchor?.name || "Your Series")
    .replace(/ · Playing Free$/, "")
    .replace(/ · Foundation$/, "")
    .replace(/ · Trust$/, "");
  const readyCount = days.filter((d) => d.audio_status === "ready").length;
  const totalMin = days.reduce((n, d) => n + (d.duration_minutes || 0), 0);
  return (
    <li className="w-full" data-testid={`series-card-${entry.group_id}`}>
      <div
        className="rounded-2xl p-4 transition-all"
        style={{
          background: "linear-gradient(135deg, rgba(215,187,122,0.09) 0%, rgba(215,187,122,0.03) 100%)",
          border: `1px solid ${TOK.gold}`,
        }}
      >
        <button
          onClick={() => setExpanded((v) => !v)}
          data-testid={`series-toggle-${entry.group_id}`}
          className="w-full text-left flex items-start gap-3"
        >
          <div className="shrink-0 rounded-full w-10 h-10 flex items-center justify-center" style={{ background: TOK.goldGlow, border: `1px solid ${TOK.gold}` }}>
            <Sparkles size={16} color={TOK.gold} />
          </div>
          <div className="flex-1">
            <p className="uppercase tracking-[0.28em] text-[0.55rem]" style={{ color: TOK.gold, fontFamily: '"Manrope", sans-serif' }}>
              3-Day Series · {readyCount}/{days.length} ready
            </p>
            <h3 className="text-xl leading-tight mt-0.5" style={{ fontFamily: '"Cormorant Garamond", serif', color: TOK.cream }}>
              {baseName}
            </h3>
            <p className="text-xs mt-1" style={{ color: TOK.creamDim, fontFamily: '"Manrope", sans-serif' }}>
              {days.map((d) => d.series_label || `Day ${d.series_day}`).join(" · ")} · {totalMin} min total
            </p>
          </div>
          <ChevronRight
            size={18}
            style={{
              color: TOK.goldSoft,
              transform: expanded ? "rotate(90deg)" : "rotate(0)",
              transition: "transform 200ms",
            }}
          />
        </button>

        {expanded && (
          <ul className="mt-4 space-y-2.5" data-testid={`series-days-${entry.group_id}`}>
            {days.map((j) => (
              <li key={j.id}>
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className="inline-flex items-center justify-center rounded-full text-[0.6rem] uppercase tracking-[0.22em]"
                    style={{
                      background: TOK.goldGlow, color: TOK.gold,
                      border: `1px solid ${TOK.gold}`,
                      fontFamily: '"Manrope", sans-serif',
                      padding: "2px 8px", fontWeight: 600,
                    }}
                    data-testid={`series-day-badge-${j.id}`}
                  >
                    Day {j.series_day || "—"} · {j.series_label || j.name}
                  </span>
                </div>
                <JourneyCard j={j} onChange={onChange} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </li>
  );
}


// ── Share Private Link ─────────────────────────────────────────
// Mints a private, unguessable /share/<slug> link and shows a
// copy-to-clipboard chip. Great for the "listen to this in my voice"
// gift moment. Backend enforces ownership and ready-state.
function SharePrivateLinkButton({ j }) {
  const [state, setState] = React.useState("idle"); // idle | loading | ready | copied | revoking | error
  const [url, setUrl] = React.useState(null);
  const [err, setErr] = React.useState(null);

  const _friendly = (detail) => {
    if (detail === "journey_not_ready") return "This installation isn't ready to share yet. Wait until it's finished rendering.";
    if (detail === "journey_not_found") return "We couldn't find that installation on your account.";
    if (detail === "slug_mint_failed") return "The link couldn't be created. Please try again.";
    return detail || "Couldn't mint the link. Please try again.";
  };

  const mint = async () => {
    setErr(null);
    setState("loading");
    try {
      const r = await axios.post(`${API}/api/audio-journeys/${j.id}/share`, {}, auth());
      const abs = `${window.location.origin}${r.data.url}`;
      setUrl(abs);
      setState("ready");
    } catch (e) {
      console.error("[share] mint failed:", e);
      setErr(_friendly(e?.response?.data?.detail));
      setState("error");
    }
  };
  const copy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setState("copied");
      setTimeout(() => setState("ready"), 1600);
    } catch (e) {
      console.warn("[share] clipboard failed:", e);
      setErr("Your browser blocked the clipboard. Long-press the link to copy.");
    }
  };
  const revoke = async () => {
    setState("revoking");
    setErr(null);
    try {
      await axios.delete(`${API}/api/audio-journeys/${j.id}/share`, auth());
      setUrl(null);
      setState("idle");
    } catch (e) {
      console.error("[share] revoke failed:", e);
      setErr("Couldn't revoke the link. Please try again.");
      setState("ready");
    }
  };

  return (
    <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid={`journey-share-panel-${j.id}`}>
      {state === "idle" && (
        <button
          onClick={mint}
          data-testid={`journey-share-mint-${j.id}`}
          className="inline-flex items-center gap-1.5 px-3 rounded-full transition-all"
          style={{
            background: "transparent",
            border: `1px solid ${TOK.goldMist}`,
            color: TOK.creamDim,
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.6rem", letterSpacing: "0.24em", textTransform: "uppercase",
            minHeight: 44,
          }}
        >
          <Share2 size={11} /> Share as a gift
        </button>
      )}
      {state === "loading" && (
        <span className="inline-flex items-center gap-1.5 text-[0.6rem] uppercase tracking-[0.22em]" style={{ color: TOK.creamDim, fontFamily: '"Manrope", sans-serif' }}>
          <Loader2 size={11} className="animate-spin" /> Minting your link
        </span>
      )}
      {(state === "ready" || state === "copied") && url && (
        <>
          <span
            className="text-[0.66rem] max-w-[240px] truncate rounded-full px-3 py-1.5"
            style={{
              background: "rgba(215,187,122,0.10)",
              border: `1px solid ${TOK.goldMist}`,
              color: TOK.cream,
              fontFamily: '"Manrope", sans-serif',
            }}
            data-testid={`journey-share-url-${j.id}`}
            title={url}
          >
            {url.replace(/^https?:\/\//, "")}
          </span>
          <button
            onClick={copy}
            data-testid={`journey-share-copy-${j.id}`}
            className="inline-flex items-center gap-1.5 px-3 rounded-full"
            style={{
              background: state === "copied" ? "rgba(215,187,122,0.22)" : TOK.gold,
              color: state === "copied" ? TOK.gold : TOK.ink,
              border: state === "copied" ? `1px solid ${TOK.gold}` : "none",
              fontFamily: '"Manrope", sans-serif',
              fontSize: "0.6rem", letterSpacing: "0.24em", textTransform: "uppercase", fontWeight: 600,
              minHeight: 44,
            }}
          >
            {state === "copied" ? <><Check size={11} /> Copied</> : "Copy link"}
          </button>
          <button
            onClick={revoke}
            disabled={state === "revoking"}
            data-testid={`journey-share-revoke-${j.id}`}
            className="text-[0.6rem] uppercase tracking-[0.22em] opacity-60 hover:opacity-100 transition-opacity inline-flex items-center px-2"
            style={{ color: TOK.creamMist, fontFamily: '"Manrope", sans-serif', minHeight: 44 }}
          >
            {state === "revoking" ? "Revoking…" : "Revoke"}
          </button>
        </>
      )}
      {err && (
        <p className="w-full text-[0.66rem] italic" style={{ color: "rgba(243,153,153,0.85)", fontFamily: '"Cormorant Garamond", serif' }} data-testid={`journey-share-error-${j.id}`}>
          {err}
        </p>
      )}
    </div>
  );
}


// ── Recompose in my voice ─────────────────────────────────────────
// Shown on any journey whose voice_bank_lines > 0 — offers a
// one-tap re-render that folds the user's own recordings into the
// composed audio. This is what solves the "I recorded 28 lines but
// the track still played the Reclaim voice" bug for older
// installations rendered before the voice-bank lookup fix landed.
function RecomposeInMyVoiceButton({ j, onChange }) {
  const [state, setState] = React.useState("idle"); // idle | requesting | queued | error
  const [err, setErr] = React.useState(null);
  const fully = j.total_script_lines > 0 && j.voice_bank_lines >= j.total_script_lines;
  const label = fully ? "Recompose in my voice" : `Recompose with ${j.voice_bank_lines} in my voice`;

  const go = async () => {
    setErr(null);
    setState("requesting");
    try {
      await axios.post(`${API}/api/audio-journeys/${j.id}/recompose`, {}, auth());
      setState("queued");
      // Give the background render a moment then refresh the list so
      // the card flips to "Rendering your audio…" and the user sees
      // progress in one place.
      setTimeout(() => onChange?.(), 500);
    } catch (e) {
      console.error("[recompose] failed:", e);
      setErr(e?.response?.data?.detail || "Couldn't start the recompose. Please try again.");
      setState("error");
    }
  };

  if (state === "queued") return null; // the card is now "rendering"

  return (
    <div className="mt-3">
      <button
        onClick={go}
        disabled={state === "requesting"}
        data-testid={`journey-recompose-${j.id}`}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all"
        style={{
          background: "rgba(215,187,122,0.10)",
          border: `1px solid ${TOK.gold}`,
          color: TOK.cream,
          fontFamily: '"Manrope", sans-serif',
          fontSize: "0.62rem",
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          opacity: state === "requesting" ? 0.7 : 1,
        }}
      >
        {state === "requesting" ? (
          <><Loader2 size={11} className="animate-spin" /> Starting…</>
        ) : (
          <><Mic size={11} /> {label}</>
        )}
      </button>
      {err && (
        <p
          className="mt-1 text-[0.66rem] italic"
          style={{ color: "rgba(243,153,153,0.85)", fontFamily: '"Manrope", sans-serif' }}
          data-testid={`journey-recompose-error-${j.id}`}
        >
          {err}
        </p>
      )}
    </div>
  );
}


// ── Explore the Full Library of Words ─────────────────────────
// Words-step secondary picker. Opens a collapsed panel where the
// user can search for another collection and MERGE its lines into
// the current approvedScript. Never replaces — always adds.
function ExploreLibraryPanel({ onMergeCollection }) {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState("");
  const [results, setResults] = React.useState(null);
  const [merging, setMerging] = React.useState(null);
  const [addedIds, setAddedIds] = React.useState([]);

  React.useEffect(() => {
    if (!open) return;
    const qn = q.trim();
    if (!qn) { setResults(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/api/installation-library/search`, { ...auth(), params: { q: qn } });
        setResults(r.data.items || []);
      } catch (e) { console.error("[explore]", e); }
    }, 220);
    return () => clearTimeout(t);
  }, [q, open]);

  const merge = async (c) => {
    setMerging(c.id);
    try {
      const r = await axios.get(
        `${API}/api/installation-library/collections/${encodeURIComponent(c.id)}`,
        auth(),
      );
      onMergeCollection(r.data);
      setAddedIds((prev) => [...prev, c.id]);
    } catch (e) {
      console.error("[explore-merge]", e);
    } finally {
      setMerging(null);
    }
  };

  return (
    <div
      className="mt-6 rounded-2xl overflow-hidden"
      style={{ background: "rgba(215,187,122,0.04)", border: `1px solid ${TOK.goldMist}` }}
      data-testid="explore-library-panel"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="explore-library-toggle"
        aria-expanded={open}
        className="w-full flex items-center gap-2 px-4 py-3 text-left"
      >
        <Search size={13} style={{ color: TOK.gold }} />
        <div className="flex-1">
          <div
            className="uppercase tracking-[0.24em] text-[0.62rem]"
            style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
          >
            Explore the Full Library of Words
          </div>
          <div className="text-[0.78rem] italic mt-0.5" style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif' }}>
            Add lines from any other collection without restarting.
          </div>
        </div>
        <ChevronRight size={14} style={{ color: TOK.gold, transform: open ? "rotate(90deg)" : "none", transition: "transform 200ms ease" }} />
      </button>

      {open && (
        <div className="px-4 pb-4 pt-2" style={{ borderTop: `1px solid ${TOK.goldMist}` }} data-testid="explore-library-body">
          <div
            className="flex items-center gap-2 px-3 py-2 rounded-lg"
            style={{ background: "rgba(245,236,217,0.03)", border: `1px solid ${TOK.goldMist}` }}
          >
            <Search size={13} style={{ color: TOK.goldSoft }} />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder='e.g. "letting go of mistakes", "self-belief", "night anxiety"'
              data-testid="explore-library-search"
              className="flex-1 bg-transparent outline-none"
              style={{ color: TOK.cream, fontFamily: '"Manrope", sans-serif', fontSize: "0.9rem" }}
            />
          </div>
          <div className="mt-3 space-y-2">
            {(results || []).slice(0, 8).map((c) => {
              const added = addedIds.includes(c.id);
              return (
                <div
                  key={c.id}
                  className="flex items-start gap-3 px-3 py-2 rounded-lg"
                  style={{ background: added ? "rgba(215,187,122,0.12)" : "rgba(245,236,217,0.02)", border: `1px solid ${added ? TOK.gold : TOK.goldMist}` }}
                  data-testid={`explore-library-row-${c.id}`}
                >
                  <div className="flex-1">
                    <div style={{ fontFamily: '"Cormorant Garamond", serif', color: TOK.cream, fontSize: "0.98rem" }}>{c.title}</div>
                    <div className="text-[0.6rem] uppercase tracking-[0.22em] mt-0.5" style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}>
                      {c.line_count} lines · ~{c.duration_est_min} min
                    </div>
                  </div>
                  <button
                    onClick={() => !added && merge(c)}
                    disabled={added || merging === c.id}
                    data-testid={`explore-library-add-${c.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[0.58rem] uppercase tracking-[0.22em]"
                    style={{
                      background: added ? "transparent" : TOK.gold,
                      color: added ? TOK.gold : TOK.ink,
                      border: added ? `1px solid ${TOK.gold}` : "none",
                      fontFamily: '"Manrope", sans-serif',
                      fontWeight: 600,
                    }}
                  >
                    {merging === c.id ? <Loader2 size={10} className="animate-spin" /> : added ? <Check size={10} /> : <Plus size={10} />}
                    {added ? "Added" : merging === c.id ? "Adding" : "Add lines"}
                  </button>
                </div>
              );
            })}
            {q.trim() && results && results.length === 0 && (
              <p className="text-[0.78rem] italic" style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif' }}>
                Nothing matches yet. Try broader terms.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


// ── Installation Library Picker ──────────────────────────────────
// Search-first discovery for Build Your Own. Every result is a
// complete pre-composed arc — never a single line. Members search
// for an area, situation, goal or desired state; picking a result
// pre-populates the whole Words step and jumps them forward.
const SPORT_LABELS = {
  golf:          "Golf",
  gym:           "Gym & Strength",
  tennis:        "Tennis",
  running:       "Running",
  martial_arts:  "Martial Arts",
  swimming:      "Swimming",
  general:       "Any Sport",
};
function InstallationLibraryPicker({ currentThemeId, onSelectCollection }) {
  const [index, setIndex] = React.useState(null);
  const [q, setQ] = React.useState("");
  const [results, setResults] = React.useState(null);
  const [searching, setSearching] = React.useState(false);
  const [activeCategory, setActiveCategory] = React.useState(null);
  const [activeSport, setActiveSport] = React.useState(null);
  const [browseCollections, setBrowseCollections] = React.useState(null);
  const [loadingCollectionId, setLoadingCollectionId] = React.useState(null);
  const [err, setErr] = React.useState(null);

  // Load the primary categories index once. iter265 fix — clear any
  // stale error on success so a transient blip doesn't linger over a
  // healthy subsequent search.
  React.useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/api/installation-library`, auth())
      .then((r) => { if (!cancelled) { setIndex(r.data); setErr(null); } })
      .catch((e) => { if (!cancelled) setErr("Couldn't load the library."); console.error(e); });
    return () => { cancelled = true; };
  }, []);

  // Debounced search. Each fresh keystroke clears any prior error
  // so a transient failure never lingers over a subsequent success.
  React.useEffect(() => {
    const qn = q.trim();
    if (!qn) { setResults(null); setSearching(false); setErr(null); return; }
    setSearching(true);
    setErr(null);
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/api/installation-library/search`, {
          ...auth(), params: { q: qn },
        });
        if (cancelled) return;
        setResults(r.data.items || []);
        setErr(null);
      } catch (e) {
        if (cancelled) return;
        console.error("[library-search]", e);
        // Softer copy — the search endpoint is well tested; the most
        // common failure is a transient network blip.
        setErr("Search didn't respond. Try again in a moment.");
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 220);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  // When category chosen (and no active search), lazy-load its collections.
  React.useEffect(() => {
    if (!activeCategory || q.trim()) { return; }
    let cancelled = false;
    setBrowseCollections(null);
    axios
      .get(`${API}/api/installation-library/collections`, { ...auth(), params: { category: activeCategory.id } })
      .then((r) => { if (!cancelled) setBrowseCollections(r.data.items || []); })
      .catch((e) => { console.error("[library-browse]", e); });
    return () => { cancelled = true; };
  }, [activeCategory, q]);

  const selectCollection = async (c) => {
    setErr(null);
    setLoadingCollectionId(c.id);
    try {
      const r = await axios.get(
        `${API}/api/installation-library/collections/${encodeURIComponent(c.id)}`,
        { ...auth(), timeout: 45000 },
      );
      const payload = r.data;
      onSelectCollection({
        collection: payload.collection,
        theme_id_for_composer: payload.theme_id_for_composer,
      }, payload.approved_script);
    } catch (e) {
      console.error("[library-select]", e);
      const detail = e?.response?.data?.detail;
      const isTimeout = e?.code === "ECONNABORTED" || (e?.message || "").includes("timeout");
      setErr(
        isTimeout
          ? "That took a while — the library was warming up. Please tap Use collection again."
          : detail
            ? `Couldn't load that collection (${detail}). Please try again.`
            : "Couldn't load that collection. Please try again."
      );
    } finally {
      setLoadingCollectionId(null);
    }
  };

  const searchActive = !!q.trim();
  const showBrowseGrid = !searchActive && !activeCategory;
  const showBrowseCollections = !searchActive && !!activeCategory;

  return (
    <div data-testid="installation-library-picker">
      {/* Search bar */}
      <div
        className="mt-3 flex items-center gap-2 px-3 py-2.5 rounded-xl"
        style={{ background: "rgba(245,236,217,0.03)", border: `1px solid ${TOK.goldMist}` }}
      >
        <Search size={16} style={{ color: TOK.goldSoft }} />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder='Try "public speaking", "golf putting", "afternoon energy"…'
          data-testid="library-search-input"
          className="flex-1 bg-transparent outline-none"
          style={{ color: TOK.cream, fontFamily: '"Manrope", sans-serif', fontSize: "0.94rem" }}
        />
        {q && (
          <button onClick={() => setQ("")} data-testid="library-search-clear" style={{ color: TOK.creamMist }} aria-label="Clear">
            <X size={14} />
          </button>
        )}
      </div>
      <p
        className="mt-2 italic text-[0.82rem] leading-relaxed"
        style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif' }}
      >
        Find the situation, goal, pattern or state you would like to work with. Each result contains a complete set of words, carefully selected and arranged for you.
      </p>

      {err && (
        <p className="mt-2 text-[0.72rem]" style={{ color: "rgba(243,153,153,0.85)", fontFamily: '"Manrope", sans-serif' }} data-testid="library-error">
          {err}
        </p>
      )}

      {/* Search results */}
      {searchActive && (
        <div className="mt-5 space-y-2" data-testid="library-search-results">
          {searching && (
            <p className="text-[0.75rem] italic" style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif' }}>Searching the library…</p>
          )}
          {!searching && results !== null && results.length === 0 && (
            <p className="text-[0.85rem] italic" style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif' }}>
              No collection matches &ldquo;{q}&rdquo; yet. Try a situation like &ldquo;presentation nerves&rdquo; or &ldquo;afternoon energy&rdquo;.
            </p>
          )}
          {(results || []).map((c) => (
            <CollectionRow
              key={c.id}
              c={c}
              loading={loadingCollectionId === c.id}
              selected={currentThemeId && c.theme_id === currentThemeId}
              onUse={() => selectCollection(c)}
            />
          ))}
        </div>
      )}

      {/* Browse by intention — categories grid */}
      {showBrowseGrid && (
        <div className="mt-6" data-testid="library-browse-grid">
          <p className="text-xs uppercase tracking-[0.22em] mb-3" style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}>
            Browse by intention
          </p>
          <div className="grid grid-cols-2 gap-2">
            {(index?.primary_categories || []).map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat)}
                data-testid={`library-cat-${cat.id}`}
                disabled={cat.collection_count === 0}
                className="text-left px-3 py-2.5 rounded-xl transition-all"
                style={{
                  background: "rgba(245,236,217,0.03)",
                  border: `1px solid ${TOK.goldMist}`,
                  opacity: cat.collection_count === 0 ? 0.45 : 1,
                  cursor: cat.collection_count === 0 ? "not-allowed" : "pointer",
                }}
              >
                <div style={{ fontFamily: '"Cormorant Garamond", serif', color: TOK.cream, fontSize: "0.98rem", lineHeight: 1.15 }}>
                  {cat.label}
                </div>
                <div className="mt-0.5 italic text-[0.7rem]" style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif' }}>
                  {cat.blurb}
                </div>
                <div className="mt-1 text-[0.6rem] uppercase tracking-[0.22em]" style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}>
                  {cat.collection_count === 0 ? "Coming soon" : `${cat.collection_count} collection${cat.collection_count === 1 ? "" : "s"}`}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Browse — collections inside a category */}
      {showBrowseCollections && (
        <div className="mt-6" data-testid="library-browse-collections">
          <button
            onClick={() => { setActiveCategory(null); setActiveSport(null); }}
            data-testid="library-back-to-categories"
            className="inline-flex items-center gap-1.5 text-[0.66rem] uppercase tracking-[0.24em] mb-3"
            style={{ color: TOK.gold, fontFamily: '"Manrope", sans-serif' }}
          >
            <ChevronRight size={11} style={{ transform: "rotate(180deg)" }} /> All categories
          </button>
          <p
            className="text-xs uppercase tracking-[0.22em] mb-2"
            style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
          >
            {activeCategory.label}
            {activeCategory.id === "sport" && activeSport ? (
              <> · <span style={{ color: TOK.cream }}>{SPORT_LABELS[activeSport] || activeSport}</span></>
            ) : null}
          </p>

          {/* Sport = extra nested step. Choose your sport first, then
              the specific focus (Golf → Putting / Bunkers / First Tee…) */}
          {activeCategory.id === "sport" && !activeSport ? (
            <>
              <p
                className="italic text-[0.82rem] mb-3 leading-relaxed"
                style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif' }}
              >
                First, choose your sport. Then we&apos;ll surface just the focuses that fit.
              </p>
              <div className="grid grid-cols-2 gap-2" data-testid="library-sport-picker">
                {(() => {
                  const bySport = {};
                  (browseCollections || []).forEach((c) => {
                    const s = c.sport || "general";
                    (bySport[s] = bySport[s] || []).push(c);
                  });
                  const order = ["golf", "gym", "tennis", "running", "martial_arts", "swimming", "general"];
                  return order.filter((s) => bySport[s]?.length).map((s) => (
                    <button
                      key={s}
                      onClick={() => setActiveSport(s)}
                      data-testid={`library-sport-${s}`}
                      className="text-left px-3 py-2.5 rounded-xl"
                      style={{
                        background: "rgba(245,236,217,0.03)",
                        border: `1px solid ${TOK.goldMist}`,
                      }}
                    >
                      <div style={{ fontFamily: '"Cormorant Garamond", serif', color: TOK.cream, fontSize: "1.02rem" }}>
                        {SPORT_LABELS[s] || s}
                      </div>
                      <div className="mt-1 text-[0.6rem] uppercase tracking-[0.22em]" style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}>
                        {bySport[s].length} focus{bySport[s].length === 1 ? "" : "es"}
                      </div>
                    </button>
                  ));
                })()}
              </div>
            </>
          ) : (
            <>
              {activeCategory.id === "sport" && activeSport && (
                <button
                  onClick={() => setActiveSport(null)}
                  data-testid="library-back-to-sports"
                  className="inline-flex items-center gap-1.5 text-[0.62rem] uppercase tracking-[0.24em] mb-2"
                  style={{ color: TOK.gold, fontFamily: '"Manrope", sans-serif' }}
                >
                  <ChevronRight size={10} style={{ transform: "rotate(180deg)" }} /> All sports
                </button>
              )}
              {browseCollections === null && (
                <p className="text-[0.75rem] italic" style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif' }}>Loading…</p>
              )}
              {browseCollections && browseCollections.length === 0 && (
                <p className="text-[0.85rem] italic" style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif' }}>
                  These collections are being composed and will appear here soon.
                </p>
              )}
              <div className="space-y-2">
                {(browseCollections || [])
                  .filter((c) => activeCategory.id !== "sport" || !activeSport || c.sport === activeSport)
                  .map((c) => (
                    <CollectionRow
                      key={c.id}
                      c={c}
                      loading={loadingCollectionId === c.id}
                      selected={currentThemeId && c.theme_id === currentThemeId}
                      onUse={() => selectCollection(c)}
                    />
                  ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}


function CollectionRow({ c, loading, selected, onUse }) {
  return (
    <div
      className="px-3 py-2.5 rounded-xl flex items-start gap-3"
      style={{
        background: selected ? "rgba(215,187,122,0.14)" : "rgba(245,236,217,0.03)",
        border: `1px solid ${selected ? TOK.gold : TOK.goldMist}`,
      }}
      data-testid={`library-collection-${c.id}`}
    >
      <div className="flex-1 min-w-0">
        <div style={{ fontFamily: '"Cormorant Garamond", serif', color: TOK.cream, fontSize: "1.04rem", lineHeight: 1.2 }}>
          {c.title}
        </div>
        {c.outcome && (
          <div className="mt-0.5 italic text-[0.78rem]" style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif' }}>
            {c.outcome}
          </div>
        )}
        <div className="mt-1 flex items-center gap-2 text-[0.6rem] uppercase tracking-[0.22em]" style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}>
          <span>{c.line_count} lines</span>
          <span>·</span>
          <span>~{c.duration_est_min} min</span>
        </div>
      </div>
      <button
        onClick={onUse}
        disabled={loading}
        data-testid={`library-use-${c.id}`}
        className="shrink-0 inline-flex items-center gap-1.5 px-4 rounded-full transition-all"
        style={{
          background: TOK.gold,
          color: TOK.ink,
          fontFamily: '"Manrope", sans-serif',
          fontSize: "0.6rem",
          letterSpacing: "0.24em",
          textTransform: "uppercase",
          fontWeight: 600,
          opacity: loading ? 0.6 : 1,
          minHeight: 44,
        }}
      >
        {loading ? <><Loader2 size={11} className="animate-spin" /> Loading</> : (selected ? <><Check size={11} /> Using</> : "Use collection")}
      </button>
    </div>
  );
}


function JourneyCard({ j, onChange, highlight = false }) {
  const nav = useNavigate();
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);
  const [buffering, setBuffering] = useState(false);
  const audioRef = useRef(null);
  const isReady = j.audio_status === "ready" && !!j.audio_url;
  const isRendering = j.audio_status === "rendering";
  const isFailed = j.audio_status === "failed";
  const fmtTime = (s) => {
    if (!isFinite(s) || s < 0) return "0:00";
    const m = Math.floor(s / 60);
    const r = Math.floor(s % 60);
    return `${m}:${r < 10 ? "0" + r : r}`;
  };
  const seekTo = (e) => {
    const el = audioRef.current;
    if (!el || !dur) return;
    // Both onClick and onTouchStart fire on touch devices; skip the
    // synthetic mouse event so we don't double-seek.
    if (e.type === "click" && e.detail === 0) return;
    if (e.cancelable) e.preventDefault?.();
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    el.currentTime = Math.max(0, Math.min(dur, (x / rect.width) * dur));
  };
  const pct = dur > 0 ? Math.min(100, (cur / dur) * 100) : 0;

  const onPlay = async () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) {
      el.pause();
      setPlaying(false);
      return;
    }
    try {
      await axios.post(`${API}/api/audio-journeys/${j.id}/play`, {}, auth());
    } catch {}
    el.play().catch(() => {});
    setPlaying(true);
    // Auto-tick Daily Essentials · Daily Audio (session-scoped dedupe).
    trackAudioPlayed();
  };

  const onFav = async () => {
    try {
      await axios.patch(`${API}/api/audio-journeys/${j.id}`, { favourited: !j.favourited }, auth());
      onChange();
    } catch {}
  };

  const onDelete = async () => {
    if (!window.confirm(`Delete "${j.name}"?`)) return;
    try {
      await axios.post(`${API}/api/audio-journeys/bulk-trash`, { ids: [j.id] }, auth());
      onChange();
    } catch {}
  };

  // Simple WhatsApp share (Michael, Feb 2026): one tap opens
  // WhatsApp with a warm pre-filled message and the user's own
  // referral link. Works on iOS, Android and desktop web thanks to
  // the universal `wa.me` scheme.
  const handleWhatsAppShare = async () => {
    let link = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    let code = null;
    try {
      const r = await axios.get(`${API}/api/referral/code`, auth());
      if (r.data?.link) link = r.data.link;
      if (r.data?.code) code = r.data.code;
    } catch { /* fall through to the plain app link — still shareable */ }
    const cleanName = (j.name || "my Reclaim journey").replace(/\s*·.*$/, "").trim();
    const msg =
      `I just built "${cleanName}" on Reclaim ✨\n\n` +
      `A Soulumo installation made from my own words. Have a listen:\n\n` +
      `${link}` +
      (code ? `\n\n(invite code · ${code})` : "");
    const url = `https://wa.me/?text=${encodeURIComponent(msg)}`;
    // A brand-new tab is friendlier than deep-linking here — some
    // browsers block scheme handlers when triggered inline.
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const totalMin = Math.round((j.total_listening_seconds || 0) / 60);
  // Extract "Day N of 3" from custom_concern for series journeys.
  // The Ask AI modal prefixes it there when the user opts into a
  // 3-day build-up.
  const dayMatch = (j.custom_concern || "").match(/^(Day \d+ of \d+)/);
  const dayBadge = dayMatch ? dayMatch[1] : null;
  return (
    <li
      className="p-5 rounded-2xl"
      style={{
        background: highlight
          ? "linear-gradient(180deg, rgba(215,187,122,0.10), rgba(215,187,122,0.03))"
          : "linear-gradient(180deg, rgba(215,187,122,0.04), rgba(215,187,122,0.01))",
        border: `1px solid ${highlight ? TOK.gold : TOK.goldMist}`,
        boxShadow: highlight ? "0 12px 32px rgba(215,187,122,0.14)" : "none",
      }}
      data-testid={`journey-card-${j.id}`}
    >
      <div className="flex items-start gap-3">
        <button
          onClick={onPlay}
          disabled={!isReady}
          className="shrink-0 w-14 h-14 rounded-full flex items-center justify-center transition-all relative"
          style={{
            background: playing ? "transparent" : isReady ? "rgba(215,187,122,0.18)" : "rgba(215,187,122,0.05)",
            border: `1px solid ${playing ? "transparent" : isReady ? TOK.gold : TOK.goldMist}`,
            color: TOK.cream,
            cursor: isReady ? "pointer" : "default",
          }}
          data-testid={`journey-play-${j.id}`}
          aria-label={playing ? "Pause installation" : "Play installation"}
        >
          {playing ? (
            <>
              {/* Living orb replaces the plain circle while playing:
                  slow breathing + concentric frequency rings.
                  Tap anywhere on the orb to pause. */}
              <InstallOrb size={56} state="playing" />
              <Pause
                size={18}
                style={{
                  position: "absolute",
                  color: TOK.ink,
                  filter: "drop-shadow(0 0 4px rgba(255,240,200,0.6))",
                  pointerEvents: "none",
                }}
              />
            </>
          ) : isRendering ? (
            <Loader2 size={18} className="animate-spin" />
          ) : (
            <Play size={18} style={{ marginLeft: 2 }} />
          )}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              {(dayBadge || j.voice_bank_lines > 0) && (
                <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                  {dayBadge && (
                    <span
                      className="inline-block text-[0.62rem] uppercase tracking-[0.28em] px-2 py-0.5 rounded-full"
                      style={{
                        color: TOK.gold,
                        background: "rgba(215,187,122,0.14)",
                        border: `1px solid ${TOK.goldMist}`,
                        fontFamily: '"Manrope", sans-serif',
                      }}
                      data-testid={`journey-day-badge-${j.id}`}
                    >
                      {dayBadge}
                    </span>
                  )}
                  {j.voice_bank_lines > 0 && (() => {
                    const fully = j.total_script_lines > 0 && j.voice_bank_lines >= j.total_script_lines;
                    return (
                      <span
                        className="inline-flex items-center gap-1 text-[0.62rem] uppercase tracking-[0.24em] px-2 py-0.5 rounded-full transition-all"
                        style={{
                          color: fully ? TOK.ink : TOK.cream,
                          background: fully ? TOK.gold : "rgba(215,187,122,0.18)",
                          border: `1px solid ${TOK.gold}`,
                          fontFamily: '"Manrope", sans-serif',
                          fontWeight: fully ? 600 : 400,
                          boxShadow: fully ? "0 0 12px rgba(215,187,122,0.35)" : "none",
                        }}
                        title={fully
                          ? "Every line in this installation plays back in your own voice"
                          : `${j.voice_bank_lines} line${j.voice_bank_lines === 1 ? "" : "s"} play back in your own voice`}
                        data-testid={`journey-voice-badge-${j.id}`}
                      >
                        <Mic size={10} strokeWidth={2.5} />
                        {fully ? "Fully in my voice" : `${j.voice_bank_lines} in your voice`}
                      </span>
                    );
                  })()}
                </div>
              )}
              <p
                className="break-words leading-tight"
                style={{
                  color: TOK.cream,
                  fontFamily: '"Cormorant Garamond", serif',
                  fontSize: "1.42rem",
                  lineHeight: 1.18,
                }}
                data-testid={`journey-title-${j.id}`}
              >
                {j.name}
              </p>
            </div>
            <button onClick={onFav} aria-label="favourite" data-testid={`journey-fav-${j.id}`} className="shrink-0 mt-1">
              <Star size={16} fill={j.favourited ? TOK.gold : "none"} color={j.favourited ? TOK.gold : TOK.goldSoft} />
            </button>
          </div>
          <p className="text-xs mt-1 uppercase tracking-[0.18em]" style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}>
            {(() => {
              // Show the target duration when the composed audio is
              // within ±90 seconds of it (a natural side-effect of
              // fades + primer). Only surface the actual composed
              // duration if the drift is large enough to notice.
              const target = j.duration_minutes;
              const secs = j.audio_duration_seconds;
              if (!secs) return `${target} min`;
              const actualMin = Math.max(1, Math.round(secs / 60));
              const drift = Math.abs(secs - target * 60);
              return drift < 90 ? `${target} min` : `${actualMin} min`;
            })()} · {j.affirmation_theme.replace(/_/g, " ")} · {j.binaural_stages.join(" → ")}
          </p>
          {isRendering && <p className="text-xs italic mt-2" style={{ color: TOK.creamDim }}>Rendering your audio… (~30 s)</p>}
          {isFailed && (
            <div className="mt-2">
              <p className="text-xs italic" style={{ color: "rgba(243,153,153,0.85)" }}>
                {j.audio_error || "Render failed."} Your curation is safe — just tap below.
              </p>
              <button
                onClick={async () => {
                  try {
                    await axios.post(`${API}/api/audio-journeys/${j.id}/retry`, {}, auth());
                    onChange();
                  } catch (e) { console.error("[retry] failed:", e); }
                }}
                data-testid={`journey-retry-${j.id}`}
                className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[0.6rem] uppercase tracking-[0.22em]"
                style={{ background: "rgba(215,187,122,0.16)", border: `1px solid ${TOK.gold}`, color: TOK.cream, fontFamily: '"Manrope", sans-serif' }}
              >
                <RotateCcw size={11} /> Retry render
              </button>
            </div>
          )}
          {isReady && (
            <div className="flex items-center gap-4 mt-3 text-xs" style={{ color: TOK.creamDim, fontFamily: '"Manrope", sans-serif' }}>
              <span><span style={{ color: TOK.gold }}>{j.sessions_count || 0}</span> sessions</span>
              <span><span style={{ color: TOK.gold }}>{totalMin}m</span> total</span>
              <span><span style={{ color: TOK.gold }}>{j.current_streak || 0}</span>-day streak</span>
            </div>
          )}
          {isReady && j.voice_bank_lines >= 3 && (
            <RecomposeInMyVoiceButton j={j} onChange={onChange} />
          )}
          {isReady && (
            <SharePrivateLinkButton j={j} />
          )}

          {isReady && j.voice_bank_lines > 0 && j.voice_bank_lines < 3 && (
            <p
              className="mt-3 text-[0.66rem] italic"
              style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif' }}
            >
              Record {3 - j.voice_bank_lines} more line{3 - j.voice_bank_lines === 1 ? "" : "s"} and this can play in your own voice.
            </p>
          )}
        </div>
        <div className="shrink-0 flex items-center gap-2">
          {isReady && (
            <button
              onClick={handleWhatsAppShare}
              aria-label="Share on WhatsApp"
              title="Share on WhatsApp with your invite link"
              className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full transition-all"
              style={{
                background: "rgba(37,211,102,0.14)",
                border: "1px solid rgba(37,211,102,0.55)",
                color: "#7cd8a3",
                fontFamily: '"Manrope", sans-serif',
                fontSize: "0.62rem",
                letterSpacing: "0.22em",
                textTransform: "uppercase",
              }}
              data-testid={`journey-whatsapp-share-${j.id}`}
            >
              <Share2 size={12} /> Share
            </button>
          )}
          <button onClick={onDelete} aria-label="delete" className="shrink-0" data-testid={`journey-delete-${j.id}`}>
            <Trash2 size={15} color={TOK.creamDim} />
          </button>
        </div>
      </div>
      {isReady && (
        <>
          {/* Elegant inline progress — replaces the browser's native
              audio controls which broke the premium feel. The wrapper
              gives a 44px vertical tap area for iOS while the visible
              bar stays a delicate 3px line. */}
          <div className="mt-4 flex items-center gap-3" data-testid={`journey-progress-${j.id}`}>
            <span
              className="shrink-0 text-[0.6rem] tabular-nums"
              style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif', minWidth: 34 }}
            >
              {fmtTime(cur)}
            </span>
            <div
              onClick={seekTo}
              onTouchStart={seekTo}
              className="flex-1 flex items-center cursor-pointer relative"
              style={{ height: 44, marginTop: -20, marginBottom: -20, touchAction: "none" }}
              role="slider"
              aria-label="Seek"
              aria-valuemin={0}
              aria-valuemax={dur || 0}
              aria-valuenow={cur}
              data-testid={`journey-seekbar-${j.id}`}
            >
              <div
                className="w-full h-[3px] rounded-full relative pointer-events-none overflow-hidden"
                style={{ background: "rgba(215,187,122,0.18)" }}
              >
                <div
                  className="absolute inset-y-0 left-0 rounded-full"
                  style={{
                    width: `${pct}%`,
                    background: TOK.gold,
                    boxShadow: playing ? "0 0 8px rgba(215,187,122,0.55)" : "none",
                    transition: "width 120ms linear",
                    opacity: buffering ? 0.4 : 1,
                  }}
                />
                {buffering && (
                  <div
                    className="absolute inset-y-0 pointer-events-none"
                    style={{
                      left: `max(0px, calc(${pct}% - 4px))`,
                      width: 24,
                      background: `linear-gradient(90deg, ${TOK.gold}00 0%, ${TOK.gold}aa 50%, ${TOK.gold}00 100%)`,
                      animation: "journeyBuffer 1.4s ease-in-out infinite",
                    }}
                    aria-hidden="true"
                    data-testid={`journey-buffering-${j.id}`}
                  />
                )}
              </div>
            </div>
            <span
              className="shrink-0 text-[0.6rem] tabular-nums"
              style={{ color: TOK.creamDim, fontFamily: '"Manrope", sans-serif', minWidth: 34, textAlign: "right" }}
            >
              {fmtTime(dur - cur)}
            </span>
          </div>
          <audio
            ref={audioRef}
            src={`${API}${j.audio_url}`}
            preload="metadata"
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onEnded={() => {
              setPlaying(false);
              setCur(0);
              // Beta feedback orchestrator — enrich with the context
              // we can harvest from the journey & current playback.
              try {
                const audioEl = audioRef.current;
                const listenedSec = audioEl ? Math.round(audioEl.duration || 0) : null;
                const totalSec = j.length_sec || (audioEl ? audioEl.duration : null);
                const completionPct = totalSec ? Math.min(100, Math.round((listenedSec / totalSec) * 100)) : null;
                window.dispatchEvent(new CustomEvent("soulumo:installation-ended", {
                  detail: {
                    journeyId: j.id,
                    installationName: j.title || j.name || null,
                    category: j.category || j.kind || null,
                    experienceType: j.blueprint_slug ? "signature" : "custom",
                    durationSec: totalSec || null,
                    listenedSec,
                    completionPct,
                    voiceId: j.voice_id || null,
                    voiceLabel: j.voice_label || null,
                    ownVoice: !!j.own_voice,
                    music: j.music_slug || j.music || null,
                    dayOrNight: j.night ? "night" : "day",
                  },
                }));
              } catch (_) { /* older browser — fine */ }
            }}
            onTimeUpdate={(e) => setCur(e.currentTarget.currentTime || 0)}
            onLoadedMetadata={(e) => setDur(e.currentTarget.duration || 0)}
            onDurationChange={(e) => setDur(e.currentTarget.duration || 0)}
            onWaiting={() => setBuffering(true)}
            onPlaying={() => setBuffering(false)}
            onCanPlay={() => setBuffering(false)}
            data-testid={`journey-audio-${j.id}`}
          />
        </>
      )}
    </li>
  );
}

/* ── Create Wizard — Life Design Ritual ──────────────────────
 * Phases: Opening · Reflection · Curation · Voice · Atmosphere · Creation
 * Each phase uses ceremonial copy per Michael's Premium Life Design
 * Ritual brief (Jun 10 2026). The technical form below still has
 * 4 underlying input steps; the phase labels above wrap them
 * elegantly so the experience FEELS like a ritual rather than a form. */

// Ceremonial step labels — these replace "Step 1 of 4" everywhere
// inside the wizard. Voice is a wrapper phase shown briefly within
// the Atmosphere step.
// Ceremonial labels per phase. Order: Reflection → Atmosphere →
// Resonance (theme) → Frequency (binaural) → Voice (curation +
// record-your-own). Theme is picked BEFORE binaural so the safety
// constraints (Delta/Theta disabled for eyes-open themes) work.
// Curation is LAST so the user has already shaped the sonic
// environment before they read / record each affirmation.
const PHASE_LABELS = ["Intention", "Words", "Music", "Frequency", "Review"];
const LAST_STEP = 4;

// ── Creation Moment ──────────────────────────────────────────────
// Takes over the wizard body from the moment the create POST
// succeeds until the audio is ready. A breathing golden orb + a
// small rotation of poetic captions ("Weaving your words · Layering
// your voice · Aligning the arc") + a soft reveal when done. Polls
// GET /audio-journeys/{jid} every ~1.5s. Users can tap "Wait in
// the background" at any time to leave the moment early — the
// wizard closes and the card still shows Rendering on the list.
function CreationMoment({ journeyId, initialJourney, onDone }) {
  const CAPTIONS = React.useMemo(() => ([
    "Weaving your words",
    "Layering your voice",
    "Tuning the frequencies",
    "Aligning the arc",
    "Landing the installation",
  ]), []);
  const [captionIdx, setCaptionIdx] = React.useState(0);
  const [status, setStatus] = React.useState(initialJourney?.audio_status || "rendering");
  const [journey, setJourney] = React.useState(initialJourney || null);
  const [errorMsg, setErrorMsg] = React.useState(null);
  const [startedAt] = React.useState(Date.now());
  const [now, setNow] = React.useState(Date.now());

  // Elapsed timer — 1s cadence, used for the caption rotation and
  // a soft "usually ready in about two minutes" reassurance.
  React.useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  // Caption cycle — swap every 4s.
  React.useEffect(() => {
    if (status !== "rendering") return undefined;
    const t = setInterval(() => setCaptionIdx((i) => (i + 1) % CAPTIONS.length), 4000);
    return () => clearInterval(t);
  }, [status, CAPTIONS.length]);
  // Poll journey status. Backs off after 60s to reduce load.
  React.useEffect(() => {
    if (!journeyId) return undefined;
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const r = await axios.get(`${API}/api/audio-journeys/${journeyId}`, auth());
        if (cancelled) return;
        setJourney(r.data);
        setStatus(r.data?.audio_status || "rendering");
        if (r.data?.audio_status === "failed") {
          setErrorMsg(r.data?.audio_error || "The composer stumbled. You can retry from your list.");
          return;
        }
        if (r.data?.audio_status === "ready") return;
      } catch (e) {
        console.warn("[creation-moment] poll failed:", e);
      }
      const delay = attempts < 30 ? 1500 : 4000;
      if (!cancelled) setTimeout(poll, delay);
    };
    const t = setTimeout(poll, 1500);
    return () => { cancelled = true; clearTimeout(t); };
  }, [journeyId]);

  const elapsedS = Math.floor((now - startedAt) / 1000);
  const isReady = status === "ready";
  const isFailed = status === "failed" || !!errorMsg;
  const title = journey?.name || initialJourney?.name || "Your installation";
  // Rough estimate: 90–120s typical. Cap ring at 95% until ready.
  const estimateS = 120;
  const ringPct = Math.min(0.95, elapsedS / estimateS);
  const installingPct = Math.max(1, Math.round(ringPct * 100));

  // ── Inline listen ────────────────────────────────────────────
  // "Listen now" plays the fresh audio right inside the moment so
  // the button label matches its icon. Falls back gracefully if the
  // browser blocks autoplay.
  const [inlinePlaying, setInlinePlaying] = React.useState(false);
  const [inlineError, setInlineError] = React.useState(null);
  const audioRef = React.useRef(null);
  const audioUrl = journey?.audio_url || null;
  const startInlineListen = async () => {
    setInlineError(null);
    let el = audioRef.current;
    if (!el) {
      el = new Audio();
      el.src = audioUrl?.startsWith("http") ? audioUrl : `${API}${audioUrl}`;
      el.preload = "auto";
      el.addEventListener("ended", () => setInlinePlaying(false));
      audioRef.current = el;
    }
    try {
      await el.play();
      setInlinePlaying(true);
    } catch (e) {
      console.warn("[creation-moment] listen play failed:", e);
      setInlineError("Your browser paused the sound. Tap play again to begin.");
    }
  };
  const [night, setNight] = React.useState(false);
  const startNight = () => {
    if (!audioRef.current) {
      const el = new Audio(audioUrl?.startsWith("http") ? audioUrl : `${API}${audioUrl}`);
      el.preload = "auto";
      el.addEventListener("ended", () => setInlinePlaying(false));
      audioRef.current = el;
    }
    const el = audioRef.current;
    el.play().then(() => { el.pause(); el.currentTime = 0; }).catch(() => {});
    setNight(true);
  };
  React.useEffect(() => () => {
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch (_) { /* ignore */ }
      audioRef.current = null;
    }
  }, []);

  // Bounded poll safety net — after ~4 minutes with no success, we
  // surface a friendlier error so the user isn't stranded on the
  // orb forever if the composer stalls.
  const isStalled = !isReady && !isFailed && elapsedS > 240;
  const effectiveFailed = isFailed || isStalled;
  const effectiveErrorMsg = errorMsg || (isStalled ? "This is taking longer than usual. You can wait in the background — your installation will still finish and appear in your list." : null);

  return (
    <div
      className="flex-1 flex flex-col items-center justify-center px-6 pb-16 pt-8 text-center"
      data-testid="creation-moment"
    >
      {/* The orb */}
      <div className="relative" style={{ width: 220, height: 220 }} aria-hidden>
        {/* Outer breath */}
        <div
          className="absolute inset-0 rounded-full"
          style={{
            background: "radial-gradient(circle, rgba(231,201,138,0.35) 0%, rgba(231,201,138,0.10) 45%, transparent 70%)",
            animation: isReady ? "creationOrbBloom 1.6s ease forwards" : "creationOrbBreath 4.2s ease-in-out infinite",
          }}
        />
        {/* Progress ring (arc) */}
        <svg viewBox="0 0 100 100" className="absolute inset-0" style={{ transform: "rotate(-90deg)" }}>
          <circle cx="50" cy="50" r="46" stroke="rgba(231,201,138,0.16)" strokeWidth="0.8" fill="none" />
          <circle
            cx="50" cy="50" r="46"
            stroke={effectiveFailed ? "#dc8b6c" : "#e7c98a"} strokeWidth="1.4" fill="none"
            strokeLinecap="round"
            strokeDasharray={`${(isReady ? 1 : ringPct) * 289} 289`}
            style={{ transition: "stroke-dasharray 900ms cubic-bezier(0.22, 0.61, 0.36, 1)" }}
          />
        </svg>
        {/* Inner glow / core */}
        <div
          className="absolute inset-0 flex items-center justify-center"
        >
          <div
            className="rounded-full"
            style={{
              width: isReady ? 120 : 90,
              height: isReady ? 120 : 90,
              transition: "width 900ms ease, height 900ms ease, box-shadow 900ms ease",
              background: "radial-gradient(circle, rgba(255,244,214,0.85) 0%, rgba(231,201,138,0.45) 60%, rgba(231,201,138,0) 100%)",
              boxShadow: isReady
                ? "0 0 90px 20px rgba(231,201,138,0.55)"
                : "0 0 45px 8px rgba(231,201,138,0.35)",
              animation: isReady ? "none" : "creationOrbPulse 3.4s ease-in-out infinite",
            }}
          />
        </div>
      </div>

      {/* Caption */}
      <p
        className="mt-10 uppercase tracking-[0.34em] text-[0.62rem]"
        style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
      >
        {isReady ? "Ready" : effectiveFailed ? "Something got in the way" : `Installing · ${installingPct}%`}
      </p>
      <p
        key={isReady ? "ready" : effectiveFailed ? "failed" : `cap-${captionIdx}`}
        className="mt-3"
        style={{
          fontFamily: '"Cormorant Garamond", serif',
          color: TOK.cream,
          fontSize: "1.9rem",
          lineHeight: 1.1,
          animation: "creationCaptionFade 700ms ease",
        }}
        data-testid="creation-moment-caption"
      >
        {isReady ? title : effectiveFailed ? "Let's try that again in a moment." : CAPTIONS[captionIdx]}
      </p>
      <p
        className="mt-4 italic text-[0.86rem] max-w-sm"
        style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif' }}
      >
        {isReady
          ? "Your installation is ready. Land into it whenever you're ready."
          : effectiveFailed
            ? effectiveErrorMsg
            : "Usually ready in about two minutes. You can wait here or step away — we'll finish quietly in the background."}
      </p>
      {inlineError && (
        <p
          className="mt-3 text-[0.76rem] italic"
          style={{ color: "#f0b4b4", fontFamily: '"Cormorant Garamond", serif' }}
          data-testid="creation-moment-listen-error"
        >
          {inlineError}
        </p>
      )}

      {/* CTAs */}
      <div className="mt-9 flex flex-col items-center gap-3">
        {isReady ? (
          <>
            {!inlinePlaying ? (
              <button
                onClick={startInlineListen}
                data-testid="creation-moment-listen"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full"
                style={{
                  background: TOK.gold, color: TOK.ink,
                  fontFamily: '"Manrope", sans-serif',
                  fontSize: "0.72rem", letterSpacing: "0.28em", textTransform: "uppercase", fontWeight: 600,
                  boxShadow: "0 12px 40px rgba(231,201,138,0.35)",
                }}
              >
                <Play size={13} fill="currentColor" /> Listen now
              </button>
            ) : null}
            {!inlinePlaying && <div className="w-64"><NightStartButton onClick={startNight} /></div>}
            {night && <NightStartOverlay onCancel={() => setNight(false)} onFinish={() => { setNight(false); startInlineListen(); }} />}
            {inlinePlaying && (
              <p
                className="text-[0.62rem] uppercase tracking-[0.28em] inline-flex items-center gap-2"
                style={{ color: TOK.gold, fontFamily: '"Manrope", sans-serif' }}
                data-testid="creation-moment-listening"
              >
                <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: TOK.gold, animation: "creationOrbPulse 1.6s ease-in-out infinite" }} />
                Now playing
              </p>
            )}
            <button
              onClick={() => onDone(journey)}
              data-testid="creation-moment-close"
              className="text-[0.62rem] uppercase tracking-[0.24em]"
              style={{ color: TOK.creamMist, fontFamily: '"Manrope", sans-serif' }}
            >
              {inlinePlaying ? "Close and keep listening" : "See in my installations"}
            </button>
          </>
        ) : (
          <button
            onClick={() => onDone(journey)}
            data-testid="creation-moment-background"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full"
            style={{
              background: "transparent",
              border: `1px solid ${TOK.goldMist}`,
              color: TOK.creamDim,
              fontFamily: '"Manrope", sans-serif',
              fontSize: "0.62rem", letterSpacing: "0.24em", textTransform: "uppercase",
            }}
          >
            Wait in the background
          </button>
        )}
        {!isReady && !effectiveFailed && (
          <p className="text-[0.6rem] uppercase tracking-[0.22em]" style={{ color: TOK.creamMist, fontFamily: '"Manrope", sans-serif' }}>
            {elapsedS < 60 ? `${elapsedS}s` : `${Math.floor(elapsedS / 60)}m ${elapsedS % 60}s`}
          </p>
        )}
      </div>

      {/* Animation keyframes scoped to the moment. */}
      <style>{`
        @keyframes creationOrbBreath {
          0%, 100% { transform: scale(1);   opacity: 0.92; }
          50%      { transform: scale(1.06); opacity: 1; }
        }
        @keyframes creationOrbPulse {
          0%, 100% { transform: scale(1);   filter: brightness(1); }
          50%      { transform: scale(1.08); filter: brightness(1.15); }
        }
        @keyframes creationOrbBloom {
          0%   { transform: scale(1);   opacity: 0.8; }
          60%  { transform: scale(1.25); opacity: 1; }
          100% { transform: scale(1.15); opacity: 1; }
        }
        @keyframes creationCaptionFade {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}


function CreateWizard({ onClose, onCreated, blueprintPreset }) {
  // -1 = opening ritual screen, then 0..LAST_STEP = the input steps.
  // When the user came in via a Signature Blueprint "Tweak", they've
  // already made the intention — skip the opening ritual and land
  // on step 0 with the form pre-filled.
  const [step, setStepRaw] = useState(blueprintPreset ? 0 : -1);
  // Whenever the wizard returns to the Intention step we clear the
  // seeded collection so a fresh preview-script fetch runs after
  // the user picks a different theme.
  const setStep = React.useCallback((next) => {
    setStepRaw((prev) => {
      const resolved = typeof next === "function" ? next(prev) : next;
      if (resolved === 0 && prev !== 0) {
        setSeedScript(null);
        setSeedKey(null);
      }
      return resolved;
    });
  }, []);
  const [catalogue, setCatalogue] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [voiceChoice, setVoiceChoice] = useState("reclaim"); // "reclaim" | "own"
  // When voiceChoice === "reclaim", pick female (Lily, backend
  // default) or male (Michael's cloned voice). Feb 24 2026.
  const [reclaimVoiceGender, setReclaimVoiceGender] = useState("female");
  // Clone My Voice: users with a ready clone default to their own voice.
  const [hasClone, setHasClone] = useState(false);
  const [cloneResetsOn, setCloneResetsOn] = useState(null);
  useEffect(() => {
    axios.get(`${API}/api/voice-clone/me`, { headers: { Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` } })
      .then((r) => {
        if (r.data.enabled && r.data.status === "ready") {
          setHasClone(true);
          if (r.data.allowance?.over_allowance) setCloneResetsOn(r.data.allowance.resets_on);
          setReclaimVoiceGender((g) => (g === "female" ? "clone" : g));
        }
      })
      .catch(() => {});
  }, []);
  // Voice delivery level (Mar 3 2026) — "audible" is the classic
  // Reclaim envelope; "whisper" drops the voice ~12 dB so it sits
  // under the music; "subliminal" pins it at −28 dBFS across the
  // whole track. When "subliminal", the render path branches to
  // the 6-hour Overnight renderer.
  const [voiceLevel, setVoiceLevel] = useState(
    blueprintPreset && blueprintPreset.overnight ? "subliminal" : "audible"
  );
  // approvedScript: 6-section object { sec: [{text, voice_clip_id?}, ...] }
  // Populated by the CurationStep once the user has reviewed lines.
  const [approvedScript, setApprovedScript] = useState(null);
  // While the audio is being rendered we take over the wizard body
  // with the Creation Moment — a breathing golden orb + staged
  // captions. Cleared when the user leaves the moment (ready or
  // "Wait in the background").
  const [creatingJourney, setCreatingJourney] = useState(null);
  // When an Installation Library collection is picked, we hand its
  // arc-ordered lines straight into CurationStep as a seed so its
  // preview-script fetch is bypassed and the collection's curated
  // words survive into the Words step. Seed key = collection id.
  const [seedScript, setSeedScript] = useState(null);
  const [seedKey, setSeedKey] = useState(null);
  const curationRef = React.useRef(null);
  const [form, setForm] = useState(() => (blueprintPreset ? {
    name: blueprintPreset.label || "",
    music_ids: blueprintPreset.music_ids || [],
    binaural_stages: blueprintPreset.binaural_stages || [],
    affirmation_theme: blueprintPreset.theme || "",
    duration_minutes: blueprintPreset.duration_minutes || 10,
    custom_concern: blueprintPreset.custom_concern || "",
  } : {
    name: "",
    music_ids: [],
    binaural_stages: [],
    affirmation_theme: "",
    duration_minutes: 10,
    custom_concern: "",
  }));

  useEffect(() => {
    axios.get(`${API}/api/audio-journeys/catalogue`, auth())
      .then((r) => setCatalogue(r.data))
      .catch(() => setCatalogue({ music: { ambient: [], user: [] }, binaural_bands: [], themes: [] }));
  }, []);

  // ── Resume banner: if a server-side draft exists, surface a
  // calm prompt to pick up where they left off. Drafts come from
  // auto-save (PUT /draft) below — survives refresh, crashes, the
  // dreaded accidental back-nav, and even device switches.
  // The banner only shows on the wizard's opening ritual (step -1)
  // so it doesn't interrupt an in-flight build.
  const [resumeDraft, setResumeDraft] = useState(null);
  const [draftChecked, setDraftChecked] = useState(false);
  useEffect(() => {
    if (blueprintPreset) { setDraftChecked(true); return; }
    let cancelled = false;
    axios.get(`${API}/api/audio-journeys/draft`, auth())
      .then((r) => { if (!cancelled) setResumeDraft(r.data?.draft || null); })
      .catch(() => { /* offline-safe */ })
      .finally(() => { if (!cancelled) setDraftChecked(true); });
    return () => { cancelled = true; };
  }, [blueprintPreset]);

  const acceptResume = () => {
    if (!resumeDraft?.state) return;
    const s = resumeDraft.state;
    setForm({
      name: s.name || "",
      music_ids: s.music_ids || [],
      binaural_stages: s.binaural_stages || [],
      affirmation_theme: s.affirmation_theme || "",
      duration_minutes: s.duration_minutes || 10,
      custom_concern: s.custom_concern || "",
    });
    if (s.approvedScript) setApprovedScript(s.approvedScript);
    if (s.voiceChoice) setVoiceChoice(s.voiceChoice);
    if (s.reclaimVoiceGender) setReclaimVoiceGender(s.reclaimVoiceGender);
    setStep(typeof resumeDraft.step === "number" ? Math.max(0, Math.min(LAST_STEP, resumeDraft.step)) : 0);
    setResumeDraft(null);
  };
  const discardResume = async () => {
    setResumeDraft(null);
    try { await axios.delete(`${API}/api/audio-journeys/draft`, auth()); } catch (_) {}
  };

  // "Build my journey from what I already have" — one-tap shortcut
  // on the Resume banner. Skips finishing the wizard: fills any
  // missing fields with sensible defaults (signature music if none
  // picked, alpha binaural arc if none chosen, theme-derived name)
  // and submits straight to /api/audio-journeys. Only meaningful if
  // the draft has at least a theme. Respects eyes-open safety: if
  // user had Delta/Theta queued but their theme is eyes-open, we
  // strip them before submit.
  const canBuildNow = !!(resumeDraft?.state?.affirmation_theme);
  const buildNow = async () => {
    if (!resumeDraft?.state || !catalogue) return;
    const s = resumeDraft.state;
    if (!s.affirmation_theme) return;
    setSubmitting(true);
    try {
      // Signature default = first 3 ambient tracks (these are the
      // soundscape backbone — work across every theme).
      const ambient = catalogue.music?.ambient || [];
      const signatureDefault = ambient.slice(0, 3).map((t) => t.id);
      const theme = (catalogue.themes || []).find((t) => t.id === s.affirmation_theme);
      let binaural = (s.binaural_stages && s.binaural_stages.length) ? [...s.binaural_stages] : ["alpha"];
      if (theme?.eyes_open_only) {
        binaural = binaural.filter((b) => b !== "delta" && b !== "theta");
        if (!binaural.length) binaural = ["beta", "alpha", "beta"];
      }
      const themeLabel = (theme?.label || s.affirmation_theme.replace(/_/g, " "));
      const payload = {
        name: (s.name && s.name.trim().length >= 2) ? s.name.trim() : `${themeLabel} Journey`,
        music_ids: (s.music_ids && s.music_ids.length) ? s.music_ids : (signatureDefault.length ? signatureDefault : ambient.slice(0, 1).map((t) => t.id)),
        binaural_stages: binaural,
        affirmation_theme: s.affirmation_theme,
        duration_minutes: s.duration_minutes || 10,
        custom_concern: s.custom_concern || null,
      };
      // Guard: backend requires music_ids min_length=1; if the
      // catalogue itself was empty (edge case), bail with a friendly
      // error rather than a 422.
      if (!payload.music_ids.length) {
        console.warn("[build-now] no music available — falling back to Resume");
        acceptResume();
        return;
      }
      if (s.approvedScript && Object.values(s.approvedScript).some((arr) => Array.isArray(arr) && arr.length > 0)) {
        payload.approved_script = s.approvedScript;
      }
      const r = await axios.post(`${API}/api/audio-journeys`, payload, auth());
      setResumeDraft(null); // backend auto-clears draft on create; clear locally too
      if (onCreated) onCreated(r.data);
      else onClose();
    } catch (e) {
      console.error("[build-now] submit failed:", e);
      // If anything blows up, fall back to standard Resume so the
      // user still gets back into their work.
      acceptResume();
    } finally {
      setSubmitting(false);
    }
  };

  // Auto-save: debounced 1.5s. Only fires once draftChecked (so we
  // don't accidentally overwrite a draft before the user has had a
  // chance to resume it) and only inside the form (step >= 0).
  // This is the safety net that turned the post-mortem's 30-minute
  // loss into a tap-to-resume.
  const autosaveTimer = useRef(null);
  useEffect(() => {
    if (!draftChecked || step < 0) return;
    if (resumeDraft) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => {
      const state = { ...form, voiceChoice, reclaimVoiceGender, approvedScript };
      axios.put(`${API}/api/audio-journeys/draft`, { state, step }, auth())
        .catch((e) => console.warn("[draft] autosave failed:", e?.message));
    }, 1500);
    return () => { if (autosaveTimer.current) clearTimeout(autosaveTimer.current); };
  }, [form, voiceChoice, reclaimVoiceGender, approvedScript, step, draftChecked, resumeDraft]);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const toggleArr = (k, v) => setForm((p) => ({
    ...p,
    [k]: p[k].includes(v) ? p[k].filter((x) => x !== v) : [...p[k], v],
  }));

  // Safety prune: when the user picks (or switches back to) an
  // eyes-open theme, strip Delta and Theta from any previously-built
  // binaural sequence. This guards the case where a user explores the
  // wizard with a deep-state theme, then goes Back to Resonance and
  // switches to Focus / Gym / Golf etc. Without this prune, the unsafe
  // bands would silently persist into Step 3's sequence chips.
  useEffect(() => {
    if (!catalogue || !form.affirmation_theme) return;
    const theme = (catalogue.themes || []).find((t) => t.id === form.affirmation_theme);
    if (!theme?.eyes_open_only) return;
    const cleaned = form.binaural_stages.filter((b) => b !== "delta" && b !== "theta");
    if (cleaned.length !== form.binaural_stages.length) {
      setForm((p) => ({ ...p, binaural_stages: cleaned }));
    }
  }, [form.affirmation_theme, catalogue]); // eslint-disable-line react-hooks/exhaustive-deps

  const canNext =
    // step 0: Intention theme (+ duration is always set with a default)
    (step === 0 && !!form.affirmation_theme) ||
    // step 1: Words — needs a selected script with at least one line
    (step === 1 && !!approvedScript && Object.values(approvedScript).some((arr) => arr.length > 0)) ||
    // step 2: Music
    (step === 2 && form.music_ids.length >= 1) ||
    // step 3: Frequency
    (step === 3 && form.binaural_stages.length >= 1) ||
    // step 4: Review — always allow submit (name is optional / auto-suggested)
    (step === 4);

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (approvedScript) payload.approved_script = approvedScript;
      // Reclaim-voice gender (Feb 24 2026). Only pass narrator_voice_id
      // for the male voice — female is the backend default (Lily).
      if (voiceChoice === "reclaim" && reclaimVoiceGender === "male") {
        payload.narrator_voice_id = "3BrRQZxtiIYX5Ytyvl1h"; // Michael v3
      }
      if (hasClone) payload.use_clone_voice = voiceChoice === "own" || reclaimVoiceGender === "clone";
      // Voice level (Mar 3 2026; whisper attenuation updated Feb 11 2026).
      // "subliminal" pins the voice at −28 dBFS across the whole track
      // and routes the render through the 6-hour Overnight renderer;
      // "whisper" now attenuates to −6 dB (linear ~0.5) so the voice
      // sits just above the 0.42 music bed instead of vanishing below
      // it — Michael reported that at −12 dB the affirmations were
      // inaudible on iPhone speakers. "audible" is the classic
      // three-phase Reclaim envelope (default) — no voice_dbfs sent.
      if (voiceLevel === "subliminal") {
        payload.voice_dbfs = -28.0;
      } else if (voiceLevel === "whisper") {
        payload.voice_dbfs = -6.0;
      }
      // Auto-suggest a working title if the user left it blank. Uses
      // the theme label + a soft descriptor so nothing lands as
      // "Untitled".
      if (!payload.name || !payload.name.trim()) {
        const themeLabel = ((catalogue?.themes || []).find((t) => t.id === form.affirmation_theme) || {}).label || "New Installation";
        payload.name = `My ${themeLabel} Installation`;
      }
      const r = await axios.post(`${API}/api/audio-journeys`, payload, auth());
      // Hand the new journey doc to the parent so it can prepend it to
      // the list instantly — no second fetch needed, no race. The parent
      // also closes the wizard from inside its onCreated handler.
      // But BEFORE handing off, show the Creation Moment — a breathing
      // golden orb + staged captions that runs until the audio is ready.
      setCreatingJourney(r.data);
      // onCreated is deferred to CreationMoment's onDone/onBackground
      // so the wizard doesn't close mid-animation.
    } catch (e) {
      // FastAPI 422s come back as `detail: [{loc, msg, type}, ...]`, which
      // would stringify to "[object Object]" if we did naive concat. Build
      // a readable summary regardless of shape.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      let msg;
      if (Array.isArray(detail)) {
        msg = detail.map((d) => d?.msg || JSON.stringify(d)).join("; ");
      } else if (detail && typeof detail === "object") {
        msg = JSON.stringify(detail);
      } else if (typeof detail === "string") {
        msg = detail;
      } else {
        msg = e?.message || "Network error — please check your connection and try again.";
      }
      const head = status ? `Couldn't save (HTTP ${status})` : "Couldn't save";
      console.error("[journeys] build failed:", e);
      alert(`${head}: ${msg}`);
    } finally {
      setSubmitting(false);
    }
  };

  const uploadMusic = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await axios.post(`${API}/api/audio-journeys/music/upload`, fd, {
        headers: { ...auth().headers, "Content-Type": "multipart/form-data" },
      });
      // Add to catalogue + auto-select
      setCatalogue((p) => ({
        ...p,
        music: { ...p.music, user: [...(p.music?.user || []), r.data] },
      }));
      toggleArr("music_ids", r.data.id);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      let msg;
      if (Array.isArray(detail)) msg = detail.map((d) => d?.msg || JSON.stringify(d)).join("; ");
      else if (detail && typeof detail === "object") msg = JSON.stringify(detail);
      else if (typeof detail === "string") msg = detail;
      else msg = e?.message || "Network error.";
      const head = status ? `Upload failed (HTTP ${status})` : "Upload failed";
      console.error("[journeys] upload failed:", e);
      alert(`${head}: ${msg}`);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[120] flex flex-col soulumo-wizard-safe"
      style={{
        background: TOK.pageBg,
        WebkitOverflowScrolling: "touch",
        // Safe-area padding for iPhone dynamic island / home indicator.
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
      data-testid="journey-wizard"
    >
      {/* Hide floating help/support FABs while the wizard is open —
          they visually clash with the ritualistic canvas on mobile. */}
      <style>{`
        [data-testid="journey-wizard"] ~ * [data-testid*="floating"],
        body:has([data-testid="journey-wizard"]) > [data-testid*="floating"],
        body:has([data-testid="journey-wizard"]) .floating-fab {
          display: none !important;
        }
        [data-testid="journey-wizard"] * { -webkit-tap-highlight-color: transparent; }
      `}</style>

      {/* Creation Moment — takes over the wizard body once the create
          POST succeeds. Renders until the user chooses to leave. */}
      {creatingJourney ? (
        <CreationMoment
          journeyId={creatingJourney.id}
          initialJourney={creatingJourney}
          onDone={(finalJourney) => {
            const j = finalJourney || creatingJourney;
            setCreatingJourney(null);
            if (onCreated) onCreated(j);
            else onClose();
          }}
        />
      ) : (<>
      {/* Header — fixed at top so phase indicator is always visible.
          Opening ritual screen (step=-1) shows no indicator. */}
      <div className="px-5 pt-8 pb-4 shrink-0">
        <div className="max-w-xl mx-auto flex items-center justify-between">
          <p className="uppercase tracking-[0.3em] text-[0.6rem]" style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}>
            {step === -1 ? "An Invitation" : PHASE_LABELS[step] || "Creation"}
          </p>
          <button onClick={onClose} aria-label="close" data-testid="journey-wizard-close"><X size={20} color={TOK.creamDim} /></button>
        </div>
        {/* Phase progress with named labels above dots (Wave E) */}
        {step >= 0 && (
          <div className="max-w-xl mx-auto mt-3" data-testid="journey-phase-dots">
            <div className="flex items-center gap-1.5 mb-1.5">
              {PHASE_LABELS.map((label, i) => (
                <div
                  key={label}
                  className="flex-1 h-[2px] rounded-full transition-all"
                  style={{
                    background: i <= step ? TOK.gold : "rgba(215,187,122,0.18)",
                  }}
                />
              ))}
            </div>
            <div className="flex items-center gap-1.5">
              {PHASE_LABELS.map((label, i) => (
                <div
                  key={label}
                  className="flex-1 text-[0.55rem] uppercase tracking-[0.18em] text-center"
                  style={{
                    color: i === step ? TOK.gold : (i < step ? TOK.creamDim : "rgba(245,224,173,0.28)"),
                    fontFamily: '"Manrope", sans-serif',
                    userSelect: "none",
                  }}
                >
                  {label}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto px-5">
        <div className="max-w-xl mx-auto pb-6">

        {/* ── Opening Ritual Screen ── */}
        {step === -1 && (
          <>
            {resumeDraft && (
              <ResumeBanner
                draft={resumeDraft}
                onResume={acceptResume}
                onDiscard={discardResume}
                onBuildNow={canBuildNow ? buildNow : null}
                submitting={submitting}
              />
            )}
            <OpeningRitualScreen onBegin={() => setStep(0)} />
          </>
        )}

        {step === 999 && (
          <Step title="Name your installation" subtitle="Take a moment. What will you call this part of your becoming?">
            <p className="italic text-sm mt-3 mb-4 leading-relaxed" style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif' }}>
              The name you give it now becomes the doorway you return to every time you press play. There is no rush — choose with intention.
            </p>
            <input
              autoFocus value={form.name} onChange={(e) => update("name", e.target.value)}
              placeholder="Morning Alignment · Gym Warrior · Driving Test Calm"
              className="w-full px-4 py-3 rounded-xl bg-transparent"
              style={{ color: TOK.cream, border: `1px solid ${TOK.goldMist}`, fontFamily: '"Cormorant Garamond", serif', fontSize: "1.3rem" }}
              data-testid="journey-name-input"
            />
            <div className="mt-6">
              <p className="text-xs uppercase tracking-[0.22em] mb-2" style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}>Duration</p>
              <div className="flex flex-wrap gap-2">
                {[5, 10, 15, 20, 30].map((m) => (
                  <button key={m} onClick={() => update("duration_minutes", m)} data-testid={`journey-dur-${m}`}
                    className="px-4 py-2 rounded-full text-xs uppercase tracking-[0.2em]"
                    style={{
                      background: form.duration_minutes === m ? "rgba(215,187,122,0.22)" : "transparent",
                      border: `1px solid ${form.duration_minutes === m ? TOK.gold : TOK.goldMist}`,
                      color: TOK.cream, fontFamily: '"Manrope", sans-serif',
                    }}>{m} min</button>
                ))}
              </div>
            </div>
          </Step>
        )}

        {step === 2 && (
          <Step title="Choose your atmosphere" subtitle="Select the environment in which your new identity will be nurtured" icon={<Music2 size={22} color={TOK.gold} />}>
            {/* Music anchoring guidance (Reclaim Installation Framework). */}
            <div
              className="mt-5 mb-3 px-4 py-3 rounded-xl"
              style={{
                background: "rgba(215,187,122,0.08)",
                border: `1px solid ${TOK.goldMist}`,
              }}
            >
              <p className="text-xs italic leading-relaxed" style={{ color: TOK.cream, fontFamily: '"Cormorant Garamond", serif' }}>
                We&apos;ve chosen three moods for your intention. <span style={{ color: TOK.gold }}>Soulumo Selected</span> is ready to go, or pick the feeling that suits you. Each one arranges three tracks into an emotional arc.
              </p>
            </div>
            {!catalogue ? <Loader2 className="animate-spin" /> : (
              <>
                {/* Mood Journey — 3 context-routed choices, first is Soulumo Selected. */}
                <MoodJourneyChoices
                  theme={form.affirmation_theme}
                  concern={form.custom_concern}
                  selectedIds={form.music_ids}
                  onSelect={(ids) => {
                    update("music_ids", ids);
                    update("_signature_music", false);
                  }}
                />

                {/* Secondary — manual pick, capped at 3 tracks. */}
                <details className="mb-4" data-testid="journey-music-manual-details">
                  <summary
                    className="cursor-pointer text-[0.66rem] uppercase tracking-[0.24em] py-1"
                    style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif', listStyle: "none" }}
                  >
                    <ChevronRight size={11} style={{ display: "inline", marginRight: 4 }} />
                    Or choose my own music journey
                  </summary>
                  <p className="mt-2 italic text-[0.76rem]" style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif' }}>
                    Pick up to three tracks. They&apos;ll play in order across the installation.
                  </p>
                  <div className="mt-3">
                    <MusicCatalogue
                      catalogue={catalogue}
                      selectedIds={form.music_ids}
                      onToggle={(id) => {
                        const current = form.music_ids || [];
                        if (current.includes(id)) {
                          toggleArr("music_ids", id);
                          update("_signature_music", false);
                        } else if (current.length >= 3) {
                          // Enforce 3-track cap by evicting the oldest pick.
                          const next = [...current.slice(1), id];
                          update("music_ids", next);
                          update("_signature_music", false);
                        } else {
                          toggleArr("music_ids", id);
                          update("_signature_music", false);
                        }
                      }}
                    />
                    <label className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-full cursor-pointer"
                      style={{ border: `1px solid ${TOK.goldMist}`, color: TOK.creamDim, fontFamily: '"Manrope", sans-serif', fontSize: "0.7rem", letterSpacing: "0.22em", textTransform: "uppercase" }}>
                      <UploadIcon size={13} /> Upload your own
                      <input type="file" accept="audio/*" style={{ display: "none" }} onChange={(e) => uploadMusic(e.target.files?.[0])} data-testid="journey-upload-input" />
                    </label>
                  </div>
                </details>
              </>
            )}
          </Step>
        )}

        {step === 3 && (
          <Step title="Choose your frequency" subtitle="A gentle brainwave carrier for the state you're installing." icon={<Brain size={22} color={TOK.gold} />}>
            <FrequencyPresetPicker form={form} update={update} catalogue={catalogue} />
          </Step>
        )}

        {step === 0 && (
          <Step title="What are you calling into your life?" subtitle="Search a situation, goal, pattern or desired state. Or browse by intention." icon={<Sparkles size={22} color={TOK.gold} />}>
            <InstallationLibraryPicker
              currentThemeId={form.affirmation_theme}
              onSelectCollection={(c, approvedScriptFromCollection) => {
                update("affirmation_theme", c.theme_id_for_composer);
                update("_selected_collection", { id: c.collection.id, title: c.collection.title });
                // Seed the Words step BEFORE we advance so the ref
                // is ready and preview-script never runs. Then
                // jump the wizard forward to Words.
                if (approvedScriptFromCollection) {
                  setSeedScript(approvedScriptFromCollection);
                  setSeedKey(c.collection.id);
                }
                setStep(1);
              }}
            />

            {/* Duration — set at intent time so it drives music-arc length,
                affirmation count and binaural stages downstream. */}
            <div className="mt-8">
              <p className="text-xs uppercase tracking-[0.22em] mb-2" style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}>Duration</p>
              <div className="flex flex-wrap gap-2">
                {[5, 10, 15, 20, 30].map((m) => (
                  <button key={m} onClick={() => update("duration_minutes", m)} data-testid={`journey-dur-${m}`}
                    className="px-4 py-2 rounded-full text-xs uppercase tracking-[0.2em]"
                    style={{
                      background: form.duration_minutes === m ? "rgba(215,187,122,0.22)" : "transparent",
                      border: `1px solid ${form.duration_minutes === m ? TOK.gold : TOK.goldMist}`,
                      color: TOK.cream, fontFamily: '"Manrope", sans-serif',
                      userSelect: "none",
                    }}>{m} min</button>
                ))}
              </div>
              <p
                className="mt-2 italic text-[0.78rem] leading-relaxed"
                style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif' }}
              >
                Your words will be carefully spaced and repeated across your chosen duration so the installation lands as one unbroken arc.
              </p>
            </div>
          </Step>
        )}

        {step === 1 && (
          <Step title="Voice my words" subtitle="Read each line. Keep what feels true. Record any in your own voice — your subconscious recognises it." icon={<Mic size={22} color={TOK.gold} />}>
            {/* Voice preference — the global default. Per-line
                recording still overrides on any line the user speaks. */}
            <VoiceChoiceCard
              choice={voiceChoice}
              onChange={setVoiceChoice}
              gender={reclaimVoiceGender}
              hasClone={hasClone}
              cloneResetsOn={cloneResetsOn}
              onGenderChange={setReclaimVoiceGender}
            />
            {/* Voice level toggle — Mar 3 2026. Audible / Whisper /
                Subliminal. Subliminal routes to the 6-hour Overnight
                renderer. */}
            <VoiceLevelCard level={voiceLevel} onChange={setVoiceLevel} durationMin={form.duration_minutes} />
            <CurationStep
              ref={curationRef}
              themeId={form.affirmation_theme}
              durationMin={form.duration_minutes}
              customConcern={form.custom_concern}
              onScriptChange={setApprovedScript}
              initialScript={seedScript}
              seedKey={seedKey}
            />
            {/* Explore the Full Library of Words — add lines from
                any other collection without leaving this installation. */}
            <ExploreLibraryPanel
              onMergeCollection={(payload) => {
                // Push straight into CurationStep — its addLine
                // handler is the single source of truth for the
                // Words step. Going via the parent's approvedScript
                // would be silently overwritten on the next emit.
                if (curationRef.current?.mergeExternalLines) {
                  curationRef.current.mergeExternalLines(payload.approved_script || {});
                }
              }}
            />
            {/* Bring-your-own lines panel — the words step is the right
                place for it: shape personal + Reclaim lines together. */}
            <MyLinesPanel />
          </Step>
        )}

        {step === 4 && (
          <Step title="Review your installation" subtitle="One breath before you create it. Change anything by stepping back." icon={<Sparkles size={22} color={TOK.gold} />}>
            <ReviewStepBYO
              form={form}
              approvedScript={approvedScript}
              catalogue={catalogue}
              voiceChoice={voiceChoice}
              onEditStep={setStep}
              onUpdateName={(v) => update("name", v)}
            />
          </Step>
        )}
        </div>
      </div>

      {/* Sticky action bar — always reachable. Hidden during the
          opening ritual screen, which has its own "Begin" CTA. */}
      {step >= 0 && (
      <div
        className="shrink-0 px-5 pt-3 border-t"
        style={{
          background: "linear-gradient(180deg, rgba(7,13,11,0.0) 0%, rgba(7,13,11,0.92) 28%, rgba(7,13,11,1) 100%)",
          borderColor: "rgba(215,187,122,0.18)",
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 1.25rem)",
        }}
      >
        <div className="max-w-xl mx-auto flex items-center justify-between">
          <button onClick={() => step === 0 ? setStep(-1) : setStep(step - 1)}
            className="px-4 py-2 text-xs uppercase tracking-[0.22em]"
            style={{ color: TOK.creamDim, fontFamily: '"Manrope", sans-serif' }}
            data-testid="journey-wizard-back">
            Back
          </button>
          {step < LAST_STEP ? (
            <button onClick={() => setStep(step + 1)} disabled={!canNext} data-testid="journey-wizard-next"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full"
              style={{
                background: canNext ? "rgba(215,187,122,0.18)" : "rgba(215,187,122,0.05)",
                border: `1px solid ${canNext ? TOK.gold : TOK.goldMist}`,
                color: TOK.cream, fontFamily: '"Manrope", sans-serif',
                fontSize: "0.72rem", letterSpacing: "0.22em", textTransform: "uppercase",
                opacity: canNext ? 1 : 0.4,
              }}>
              Continue <ChevronRight size={14} />
            </button>
          ) : (
            <button onClick={submit} disabled={!canNext || submitting} data-testid="journey-wizard-submit"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full"
              style={{
                background: canNext ? "rgba(215,187,122,0.28)" : "rgba(215,187,122,0.05)",
                border: `1px solid ${TOK.gold}`,
                color: TOK.cream, fontFamily: '"Manrope", sans-serif',
                fontSize: "0.72rem", letterSpacing: "0.22em", textTransform: "uppercase",
                opacity: canNext && !submitting ? 1 : 0.4,
              }}>
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              {submitting ? "Weaving your installation…" : "Create My Installation"}
            </button>
          )}
        </div>
      </div>
      )}
      </>)}
    </motion.div>
  );
}

function Step({ title, subtitle, icon, children }) {
  return (
    <div>
      <div className="flex items-center gap-2">
        {icon}
        <h2 className="font-light" style={{ fontFamily: '"Cormorant Garamond", serif', fontSize: "2rem", color: TOK.cream }}>{title}</h2>
      </div>
      <p className="italic mt-1" style={{ fontFamily: '"Cormorant Garamond", serif', color: TOK.creamDim, fontSize: "1rem" }}>{subtitle}</p>
      {children}
    </div>
  );
}

// ── Grouped music catalogue with 20-s ▶ preview + ★ Signature badge.
// Auto-classifies into the 6 categories backend tagged each track with
// (Cinematic / Meditational / Upbeat / Drone / Nature / Vocal +
// "Your uploads" + "Ambient" fallback). Tapping ▶ on one track stops
// any other previewing track — one preview at a time. Auto-stops at
// 20 seconds. Jun 9 2026.
function MusicCatalogue({ catalogue, selectedIds, onToggle }) {
  const [previewingId, setPreviewingId] = useState(null);
  const audioRef = useRef(null);
  const stopTimerRef = useRef(null);

  const ambient = catalogue?.music?.ambient || [];
  const user = catalogue?.music?.user || [];

  // Category order — signature-heavy categories first.
  const CATEGORY_ORDER = ["Cinematic", "Meditational", "Drone", "Upbeat", "Nature", "Vocal", "Ambient"];

  const grouped = {};
  if (user.length) grouped["Your uploads"] = user;
  ambient.forEach((t) => {
    const cat = t.category || "Ambient";
    (grouped[cat] ||= []).push(t);
  });

  // Sort within each group: signatures first, then alphabetical.
  Object.values(grouped).forEach((arr) => {
    arr.sort((a, b) => {
      if (a.signature !== b.signature) return a.signature ? -1 : 1;
      return (a.label || "").localeCompare(b.label || "");
    });
  });

  const ordered = ["Your uploads", ...CATEGORY_ORDER]
    .filter((c) => grouped[c]?.length)
    .map((c) => ({ category: c, tracks: grouped[c] }));

  function togglePreview(track) {
    // Stop any current preview.
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch (_) {}
      audioRef.current = null;
    }
    if (stopTimerRef.current) {
      clearTimeout(stopTimerRef.current);
      stopTimerRef.current = null;
    }
    if (previewingId === track.id) {
      setPreviewingId(null);
      return;
    }
    const a = new Audio((process.env.REACT_APP_BACKEND_URL || "") + track.preview_url);
    a.volume = 0.7;
    a.play().catch((e) => console.warn("[journey] preview failed:", e));
    audioRef.current = a;
    setPreviewingId(track.id);
    stopTimerRef.current = setTimeout(() => {
      try { a.pause(); } catch (_) {}
      setPreviewingId(null);
    }, 20_000);
    a.addEventListener("ended", () => setPreviewingId(null), { once: true });
  }

  // Clean up on unmount.
  useEffect(() => () => {
    if (audioRef.current) { try { audioRef.current.pause(); } catch (_) {} }
    if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
  }, []);

  return (
    <div className="mt-3 space-y-4" data-testid="journey-music-list">
      {ordered.map(({ category, tracks }) => (
        <div key={category} data-testid={`journey-music-group-${category.replace(/\s+/g, "-")}`}>
          <p
            className="uppercase tracking-[0.28em] mb-1.5 py-1"
            style={{
              color: TOK.goldSoft,
              fontFamily: '"Manrope", sans-serif',
              fontSize: "0.58rem",
              borderBottom: `1px solid ${TOK.goldMist}`,
            }}
          >
            {category}
            <span className="ml-2 italic" style={{ color: TOK.creamDim, letterSpacing: "0.05em", textTransform: "none", fontFamily: '"Cormorant Garamond", serif' }}>{tracks.length}</span>
          </p>
          <div className="space-y-1">
            {tracks.map((t) => {
              const selected = selectedIds.includes(t.id);
              const previewing = previewingId === t.id;
              const order = selected ? selectedIds.indexOf(t.id) + 1 : null;
              return (
                <div
                  key={t.id}
                  className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg"
                  style={{
                    background: selected ? "rgba(215,187,122,0.16)" : "transparent",
                    border: `1px solid ${selected ? TOK.gold : TOK.goldMist}`,
                  }}
                >
                  {/* Play preview button — small, doesn't trigger select */}
                  <button
                    onClick={(e) => { e.stopPropagation(); togglePreview(t); }}
                    data-testid={`journey-music-preview-${t.id}`}
                    className="shrink-0 inline-flex items-center justify-center rounded-full"
                    style={{
                      width: 30,
                      height: 30,
                      background: previewing ? TOK.gold : "rgba(215,187,122,0.18)",
                      border: `1px solid ${previewing ? TOK.gold : TOK.goldMist}`,
                      color: previewing ? TOK.ink : TOK.cream,
                    }}
                    aria-label={previewing ? "Stop preview" : "Play 20-second preview"}
                  >
                    {previewing ? (
                      <span className="block" style={{ width: 8, height: 8, background: TOK.ink }} />
                    ) : (
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M5 3l14 9-14 9V3z" /></svg>
                    )}
                  </button>
                  {/* Track button — selects/deselects */}
                  <button
                    onClick={() => onToggle(t.id)}
                    data-testid={`journey-music-${t.id}`}
                    className="flex-1 flex items-start gap-2 text-left"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span style={{ color: TOK.cream, fontFamily: '"Cormorant Garamond", serif', fontSize: "1.05rem" }}>{t.label}</span>
                        {t.signature && (
                          <span
                            className="text-[0.55rem] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-full"
                            style={{ background: "rgba(215,187,122,0.18)", color: TOK.gold, fontFamily: '"Manrope", sans-serif', border: `1px solid ${TOK.gold}` }}
                            title="Intentionally tuned to your words"
                          >
                            ★ Signature
                          </span>
                        )}
                        {previewing && (
                          <span className="text-[0.55rem] uppercase tracking-[0.18em] italic" style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}>
                            previewing…
                          </span>
                        )}
                      </div>
                      {t.intent && (
                        <p
                          className="text-[0.72rem] italic mt-0.5"
                          style={{ color: TOK.creamDim, fontFamily: '"Manrope", sans-serif', lineHeight: 1.35 }}
                        >
                          {t.intent}
                        </p>
                      )}
                    </div>
                    {selected && <span className="ml-auto text-xs" style={{ color: TOK.gold }}>{order}</span>}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}


// ── Bring-your-own affirmation library panel.
// Lives in step 4 of the wizard. Lines are saved per-user, tagged to one
// of the 6 Reclaim Installation sections, and auto-merged into the
// matching section when a new journey is built. Jun 9 2026.
const SECTION_OPTIONS = [
  { id: "truth",            label: "Truth",            hint: "Evidence of what you've already done" },
  { id: "nothing_missing",  label: "Nothing Missing",  hint: "Remove pressure / striving" },
  { id: "trust",            label: "Trust",            hint: "Self-trust statements" },
  { id: "identity",         label: "Identity",         hint: "I am ___" },
  { id: "performance",      label: "Performance",      hint: "How identity expresses itself" },
  { id: "final_loop",       label: "Final Loop",       hint: "Short, rhythmic, repeatable" },
];

// ── ReviewStepBYO ─────────────────────────────────────────────────
// The elegant summary shown BEFORE the user hits Create. Progressive
// disclosure: one glance shows everything, with "Review My …" links
// that jump back to the relevant step. Working title is optional
// and defaults to a friendly auto-suggestion at submit time.
function ReviewStepBYO({ form, approvedScript, catalogue, voiceChoice, onEditStep, onUpdateName }) {
  const theme = (catalogue?.themes || []).find((t) => t.id === form.affirmation_theme);
  const collectionTitle = form._selected_collection?.title;
  const intentionLabel = collectionTitle || theme?.label || "your intention";
  const workingTitleSeed = collectionTitle || theme?.label || "your intention";
  const selectedCount = approvedScript
    ? Object.values(approvedScript).reduce((n, arr) => n + (arr?.length || 0), 0)
    : 0;
  const musicIds = form.music_ids || [];
  // Look up human labels for the picked music ids.
  const ambientById = React.useMemo(() => {
    const map = new Map();
    (catalogue?.music?.ambient || []).forEach((t) => map.set(t.id, t.label || t.id));
    return map;
  }, [catalogue]);
  const musicLabels = musicIds.map((id) => ambientById.get(id) || id.replace(/^ambient:/, "").replace(/_/g, " "));
  const isSignature = !!form._signature_music;
  const binauralStages = form.binaural_stages || [];
  const _matchesPreset = (p) =>
    p.stages.length === binauralStages.length &&
    p.stages.every((s, i) => s === binauralStages[i]);
  const _preset = FREQ_PRESETS.find(_matchesPreset);
  const binauralLabel = binauralStages[0] === "none"
    ? "No binaural"
    : _preset
      ? `${_preset.label} · ${_preset.hint.replace(/^([^·]+)·\s*/, "$1").trim()}`
      : binauralStages.join(" → ");
  const suggestedTitle = `My ${workingTitleSeed} Installation`;

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs uppercase tracking-[0.22em] mb-2" style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}>
          Working title <span style={{ opacity: 0.6, textTransform: "none", letterSpacing: 0 }}>· optional</span>
        </p>
        <input
          value={form.name}
          onChange={(e) => onUpdateName(e.target.value)}
          placeholder={suggestedTitle}
          className="w-full px-4 py-3 rounded-xl bg-transparent"
          style={{
            color: TOK.cream, border: `1px solid ${TOK.goldMist}`,
            fontFamily: '"Cormorant Garamond", serif', fontSize: "1.25rem",
          }}
          data-testid="journey-review-name-input"
        />
        <p className="text-[0.72rem] italic mt-1" style={{ color: TOK.creamDim, fontFamily: '"Manrope", sans-serif' }}>
          Leave blank and we&apos;ll call it <span style={{ color: TOK.cream }}>{suggestedTitle}</span>.
        </p>
      </div>

      <div
        className="rounded-2xl p-5"
        style={{ background: "rgba(215,187,122,0.04)", border: `1px solid ${TOK.goldMist}` }}
        data-testid="journey-review-summary"
      >
        <div className="text-[0.62rem] uppercase tracking-[0.28em] mb-3" style={{ color: TOK.gold, fontFamily: '"Manrope", sans-serif' }}>
          Your Installation
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" style={{ fontFamily: '"Manrope", sans-serif', color: TOK.cream }}>
          <ReviewRow label="Intention" value={intentionLabel} onEdit={() => onEditStep(0)} />
          <ReviewRow label="Duration" value={`${form.duration_minutes} min`} onEdit={() => onEditStep(0)} />
          <ReviewRow
            label="Words"
            value={selectedCount ? `${selectedCount} lines · arranged across ${form.duration_minutes} minutes` : "—"}
            onEdit={() => onEditStep(1)}
          />
          <ReviewRow
            label="Music"
            value={
              musicIds.length === 0 ? "—" :
              isSignature
                ? `Reclaim Signature Journey · ${musicLabels.slice(0, 3).join(" → ")}`
                : musicLabels.slice(0, 3).join(" · ")
            }
            onEdit={() => onEditStep(2)}
          />
          <ReviewRow label="Frequency" value={binauralLabel || "—"} onEdit={() => onEditStep(3)} />
          <ReviewRow label="Voice" value={voiceChoice === "reclaim" ? "Soulumo voice" : "My voice where recorded"} onEdit={() => onEditStep(1)} />
        </div>
        <p
          className="mt-3 italic text-[0.78rem]"
          style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif' }}
        >
          Your words will be carefully spaced and repeated across your chosen duration so the whole installation lands as one unbroken arc.
        </p>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="journey-review-actions">
        <ReviewChip label="Review My Words"     onClick={() => onEditStep(1)} testId="review-words"     />
        <ReviewChip label="Review My Music"     onClick={() => onEditStep(2)} testId="review-music"     />
        <ReviewChip label="Review My Frequency" onClick={() => onEditStep(3)} testId="review-frequency" />
        <ReviewChip label="Refine My Intention" onClick={() => onEditStep(0)} testId="review-intention" />
      </div>

      <p className="italic text-sm" style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif' }}>
        Usually ready in about two minutes.
      </p>
    </div>
  );
}

function ReviewRow({ label, value, onEdit }) {
  return (
    <button
      onClick={onEdit}
      className="text-left p-3 rounded-xl transition-all"
      style={{ background: "rgba(245,236,217,0.02)", border: `1px solid ${TOK.goldMist}` }}
    >
      <div className="text-[0.6rem] uppercase tracking-[0.24em]" style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}>
        {label}
      </div>
      <div className="mt-1" style={{ fontFamily: '"Cormorant Garamond", serif', fontSize: "1.08rem", color: TOK.cream }}>
        {value}
      </div>
    </button>
  );
}

function ReviewChip({ label, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all"
      style={{
        background: "transparent", border: `1px solid ${TOK.goldMist}`, color: TOK.cream,
        fontFamily: '"Manrope", sans-serif', fontSize: "0.68rem", letterSpacing: "0.18em", textTransform: "uppercase",
      }}
    >
      {label}
    </button>
  );
}


function MyLinesPanel() {
  const [lines, setLines] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [section, setSection] = useState("identity");
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const reload = async () => {
    try {
      const r = await axios.get(`${API}/api/audio-journeys/library/lines`, auth());
      setLines(r.data?.items || []);
    } catch (e) {
      console.error("[my-lines] load failed:", e);
    }
  };

  useEffect(() => { reload(); }, []);

  const save = async () => {
    if (!text.trim() || text.trim().length < 3) {
      setError("Lines need at least 3 characters.");
      return;
    }
    setError(null); setSaving(true);
    try {
      await axios.post(`${API}/api/audio-journeys/library/lines`, { section, text: text.trim() }, auth());
      setText("");
      await reload();
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't save the line. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (lid) => {
    try {
      await axios.delete(`${API}/api/audio-journeys/library/lines/${lid}`, auth());
      await reload();
    } catch (e) {
      console.error("[my-lines] delete failed:", e);
    }
  };

  const byCount = lines.length;

  return (
    <div className="mt-6 pt-5" style={{ borderTop: `1px solid ${TOK.goldMist}` }} data-testid="journey-my-lines-panel">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between text-left"
        data-testid="journey-my-lines-toggle"
      >
        <div>
          <p className="uppercase tracking-[0.22em]" style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif', fontSize: "0.65rem" }}>
            Bring your own lines
          </p>
          <p className="italic text-sm mt-1" style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif' }}>
            {byCount === 0
              ? "Add your own words — they'll be woven into the matching section of every installation."
              : `${byCount} saved line${byCount === 1 ? "" : "s"} · all included by default.`}
          </p>
        </div>
        <ChevronRight size={16} style={{ color: TOK.goldSoft, transform: expanded ? "rotate(90deg)" : "rotate(0)", transition: "transform 200ms" }} />
      </button>

      {expanded && (
        <div className="mt-4 space-y-3">
          <div className="p-4 rounded-xl" style={{ background: "rgba(215,187,122,0.06)", border: `1px solid ${TOK.goldMist}` }}>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {SECTION_OPTIONS.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setSection(s.id)}
                  data-testid={`journey-my-lines-section-${s.id}`}
                  className="px-2.5 py-1 rounded-full text-[0.6rem] uppercase tracking-[0.18em]"
                  style={{
                    background: section === s.id ? "rgba(215,187,122,0.24)" : "transparent",
                    border: `1px solid ${section === s.id ? TOK.gold : TOK.goldMist}`,
                    color: TOK.cream,
                    fontFamily: '"Manrope", sans-serif',
                  }}
                >
                  {s.label}
                </button>
              ))}
            </div>
            <p className="italic text-xs mb-2" style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif' }}>
              {SECTION_OPTIONS.find((s) => s.id === section)?.hint}
            </p>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Type your line · spoken in your own words"
              data-testid="journey-my-lines-input"
              className="w-full px-3 py-2 rounded-lg"
              style={{
                background: "transparent",
                border: `1px solid ${TOK.goldMist}`,
                color: TOK.cream,
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: "1.05rem",
                minHeight: "3.5rem",
                resize: "vertical",
              }}
              maxLength={160}
            />
            <div className="flex items-center justify-between mt-2">
              <span className="text-[0.6rem] italic" style={{ color: TOK.creamDim }}>{text.length}/160</span>
              <button
                onClick={save}
                disabled={saving || !text.trim()}
                data-testid="journey-my-lines-save"
                className="px-4 py-1.5 rounded-full text-[0.65rem] uppercase tracking-[0.22em]"
                style={{
                  background: text.trim() && !saving ? "rgba(215,187,122,0.24)" : "transparent",
                  border: `1px solid ${text.trim() && !saving ? TOK.gold : TOK.goldMist}`,
                  color: TOK.cream,
                  fontFamily: '"Manrope", sans-serif',
                  opacity: text.trim() && !saving ? 1 : 0.5,
                }}
              >
                {saving ? "Saving…" : "Save line"}
              </button>
            </div>
            {error && <p className="text-xs italic mt-2" style={{ color: "#dc8b6c" }}>{error}</p>}
          </div>

          {SECTION_OPTIONS.map((s) => {
            const ls = lines.filter((l) => l.section === s.id);
            if (!ls.length) return null;
            return (
              <div key={s.id} data-testid={`journey-my-lines-group-${s.id}`}>
                <p className="uppercase tracking-[0.22em] mt-3 mb-1.5" style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif', fontSize: "0.55rem" }}>
                  {s.label}
                </p>
                <div className="space-y-1">
                  {ls.map((l) => (
                    <div
                      key={l.id}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg group"
                      style={{ background: "rgba(215,187,122,0.05)", border: `1px solid ${TOK.goldMist}` }}
                    >
                      <span className="flex-1 text-sm" style={{ color: TOK.cream, fontFamily: '"Cormorant Garamond", serif' }}>{l.text}</span>
                      <button
                        onClick={() => remove(l.id)}
                        data-testid={`journey-my-lines-remove-${l.id}`}
                        className="opacity-40 group-hover:opacity-100 transition-opacity"
                        aria-label="Remove line"
                      >
                        <X size={13} style={{ color: TOK.creamDim }} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Curation Step (Voice phase) ─────────────────────────────────
// Fetches the auto-generated 6-section script via /preview-script,
// renders one expandable section per Reclaim Installation phase.
// Every line carries a keep / skip toggle AND a record-your-own-voice
// button (MediaRecorder → backend upload). The composed `approved_script`
// is propagated up via `onScriptChange` so the wizard can submit it.
// ── FrequencyPresetPicker (Wave B) ───────────────────────────────
// Preset-first frequency picker. Users pick a named intention (Calm
// Focus, Sleep and Restoration, etc.). Only Advanced reveals the
// full multi-stage builder.
const FREQ_PRESETS = [
  { id: "calm_focus",         label: "Calm Focus",            stages: ["alpha"],           hint: "Alpha throughout · gentle sharpness" },
  { id: "active_performance", label: "Active Performance",    stages: ["alpha", "beta"],   hint: "Alpha into Beta · engaged clarity" },
  { id: "deep_meditation",    label: "Deep Meditation",       stages: ["alpha", "theta"],  hint: "Alpha into Theta · receptive stillness", deepState: true },
  { id: "sleep_restoration",  label: "Sleep and Restoration", stages: ["theta", "delta"],  hint: "Theta into Delta · surrender",           deepState: true },
  { id: "no_binaural",        label: "No Binaural",           stages: ["none"],            hint: "Music and voice only" },
];

function FrequencyPresetPicker({ form, update, catalogue }) {
  const theme = (catalogue?.themes || []).find((t) => t.id === form.affirmation_theme);
  const eyesOpen = !!theme?.eyes_open_only;
  const [advanced, setAdvanced] = React.useState(false);
  const currentId = React.useMemo(() => {
    const cur = (form.binaural_stages || []).join(",");
    const match = FREQ_PRESETS.find((p) => p.stages.join(",") === cur);
    return match?.id || null;
  }, [form.binaural_stages]);

  const canPick = (p) => !(p.deepState && eyesOpen);
  const usesDeepState = (form.binaural_stages || []).some((s) => s === "theta" || s === "delta");

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-2" data-testid="freq-presets">
        {FREQ_PRESETS.map((p) => {
          const active = currentId === p.id;
          const disabled = !canPick(p);
          return (
            <button
              key={p.id}
              disabled={disabled}
              onClick={() => update("binaural_stages", p.stages)}
              data-testid={`freq-preset-${p.id}`}
              className="w-full text-left p-4 rounded-xl transition-all"
              style={{
                background: active ? "rgba(215,187,122,0.16)" : "rgba(245,236,217,0.02)",
                border: `1px solid ${active ? TOK.gold : TOK.goldMist}`,
                opacity: disabled ? 0.35 : 1,
                cursor: disabled ? "not-allowed" : "pointer",
                userSelect: "none",
              }}
            >
              <div className="flex items-baseline justify-between">
                <span style={{ fontFamily: '"Cormorant Garamond", serif', fontSize: "1.15rem", color: TOK.cream }}>
                  {p.label}
                </span>
                {disabled && (
                  <span className="text-[0.55rem] uppercase tracking-[0.22em]" style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}>
                    Not for this intention
                  </span>
                )}
              </div>
              <div className="text-[0.72rem] italic mt-1" style={{ color: TOK.creamDim, fontFamily: '"Manrope", sans-serif' }}>
                {p.hint}
              </div>
            </button>
          );
        })}
      </div>

      {usesDeepState && (
        <div
          className="p-3.5 rounded-xl"
          style={{ background: "rgba(215,187,122,0.06)", border: `1px solid ${TOK.goldMist}` }}
          data-testid="freq-safety-note"
        >
          <p className="uppercase tracking-[0.22em] text-[0.55rem] mb-1" style={{ color: TOK.gold, fontFamily: '"Manrope", sans-serif' }}>Please note</p>
          <p className="text-xs italic leading-relaxed" style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif' }}>
            For rest or eyes-closed listening only. Do not use while driving or performing tasks requiring full attention.
          </p>
        </div>
      )}

      <div>
        <button
          onClick={() => setAdvanced((v) => !v)}
          data-testid="freq-advanced-toggle"
          className="text-[0.68rem] uppercase tracking-[0.22em]"
          style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
        >
          {advanced ? "Hide advanced builder ↑" : "Build my own frequency arc · Advanced ↓"}
        </button>
      </div>

      {advanced && (
        <div className="space-y-3" data-testid="freq-advanced-builder">
          {form.binaural_stages.length > 0 && form.binaural_stages[0] !== "none" && (
            <div className="flex flex-wrap items-center gap-1.5">
              {form.binaural_stages.map((stage, idx) => (
                <React.Fragment key={`${stage}-${idx}`}>
                  {idx > 0 && <span style={{ color: TOK.goldSoft }}>→</span>}
                  <button
                    onClick={() => update("binaural_stages", form.binaural_stages.filter((_, i) => i !== idx))}
                    data-testid={`journey-binaural-chip-${idx}`}
                    className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs uppercase tracking-[0.2em]"
                    style={{ background: "rgba(215,187,122,0.22)", border: `1px solid ${TOK.gold}`, color: TOK.cream, fontFamily: '"Manrope", sans-serif' }}
                    title="Tap to remove"
                  >
                    {stage} <sup style={{ fontSize: "0.55rem", opacity: 0.7 }}>{idx + 1}</sup>
                    <X size={10} className="opacity-50" />
                  </button>
                </React.Fragment>
              ))}
            </div>
          )}
          <div className="space-y-2">
            {(catalogue?.binaural_bands || []).map((b) => {
              const disabledByEyesOpen = eyesOpen && (b.id === "delta" || b.id === "theta");
              const count = form.binaural_stages.filter((s) => s === b.id).length;
              const noBinauralActive = form.binaural_stages[0] === "none";
              const isDisabled = disabledByEyesOpen || form.binaural_stages.length >= 6 || noBinauralActive;
              return (
                <button
                  key={b.id}
                  onClick={() => {
                    if (isDisabled) return;
                    const stages = form.binaural_stages.filter((s) => s !== "none");
                    update("binaural_stages", [...stages, b.id]);
                  }}
                  disabled={isDisabled}
                  data-testid={`journey-band-${b.id}`}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-left"
                  style={{
                    background: count > 0 && !noBinauralActive ? "rgba(215,187,122,0.10)" : "transparent",
                    border: `1px solid ${count > 0 && !noBinauralActive ? TOK.gold : TOK.goldMist}`,
                    opacity: isDisabled ? 0.32 : 1,
                    cursor: isDisabled ? "not-allowed" : "pointer",
                  }}
                >
                  <div>
                    <p style={{ color: TOK.cream, fontFamily: '"Cormorant Garamond", serif', fontSize: "1.05rem" }}>
                      {b.label}
                      {disabledByEyesOpen && (
                        <span className="ml-2 text-[0.55rem] uppercase tracking-[0.22em]" style={{ color: TOK.goldSoft }}>Rest only</span>
                      )}
                    </p>
                    <p className="text-xs italic" style={{ color: TOK.creamDim }}>{b.soul}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {count > 0 && !noBinauralActive && (
                      <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: "rgba(215,187,122,0.22)", color: TOK.gold, fontFamily: '"Manrope", sans-serif' }}>
                        ×{count}
                      </span>
                    )}
                    <Plus size={14} style={{ color: isDisabled ? TOK.creamDim : TOK.goldSoft }} />
                  </div>
                </button>
              );
            })}
          </div>
          <p className="text-xs italic" style={{ color: TOK.creamDim }}>
            Tap order becomes playback order. Up to 6 stages.
          </p>
        </div>
      )}
    </div>
  );
}


function CostCell({ label, value, accent, hint }) {
  return (
    <div className="px-2 py-1.5 rounded-md" style={{ background: "rgba(215,187,122,0.04)", border: `1px solid ${TOK.goldMist}` }}>
      <p className="text-[0.5rem] uppercase tracking-[0.22em]" style={{ color: TOK.creamDim, fontFamily: '"Manrope", sans-serif' }}>{label}</p>
      <p className="mt-0.5 font-light" style={{ color: accent, fontFamily: '"Cormorant Garamond", serif', fontSize: "1.05rem", lineHeight: 1 }}>
        {value}
      </p>
      {hint && (
        <p className="text-[0.5rem] mt-0.5" style={{ color: TOK.creamDim, fontFamily: '"Manrope", sans-serif' }}>{hint}</p>
      )}
    </div>
  );
}

const SECTION_FLOW = [
  { id: "truth",            label: "Truth",            blurb: "Evidence — what you've already lived." },
  { id: "nothing_missing",  label: "Nothing Missing",  blurb: "Releasing pressure, striving, lack." },
  { id: "trust",            label: "Trust",            blurb: "Permission to trust yourself." },
  { id: "identity",         label: "Identity",         blurb: "Short. I am ___." },
  { id: "performance",      label: "Performance",      blurb: "How that identity expresses itself." },
  { id: "final_loop",       label: "Final Loop",       blurb: "Short, rhythmic, memorable. Anchored on the way out." },
];

function GoldPlayer({ src, testId, compact = false }) {
  // Minimal, elegant audio player — gold line + play/pause circle.
  // Replaces the browser's default controls which were too generic
  // and off-brand for the wizard's Voice-My-Words step.
  //
  // WebM/Opus recordings from MediaRecorder ship without a duration
  // cue, so <audio>.duration is Infinity until the browser scans the
  // whole file. Without the workaround below the player showed 0:00
  // and refused to play a user's own recording (iter265 scar).
  const audioRef = React.useRef(null);
  const [playing, setPlaying] = React.useState(false);
  const [dur, setDur] = React.useState(0);
  const [cur, setCur] = React.useState(0);
  const [scanned, setScanned] = React.useState(false);
  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) { a.play().catch(() => {}); } else { a.pause(); }
  };
  const applyDuration = (a) => {
    if (!a) return;
    const d = a.duration;
    if (isFinite(d) && d > 0) {
      setDur(d);
      setScanned(true);
      return;
    }
    // Force a scan — seek to a huge time, wait for durationchange to
    // land a real value, then rewind to 0.
    const onUpdate = () => {
      const real = a.duration;
      if (isFinite(real) && real > 0) {
        a.removeEventListener("durationchange", onUpdate);
        a.removeEventListener("timeupdate", onUpdate);
        try { a.currentTime = 0; } catch { /* noop */ }
        setDur(real);
        setScanned(true);
      }
    };
    a.addEventListener("durationchange", onUpdate);
    a.addEventListener("timeupdate", onUpdate);
    try { a.currentTime = 1e101; } catch { /* some browsers throw */ }
  };
  const fmt = (s) => {
    if (!isFinite(s) || s < 0) return "0:00";
    const m = Math.floor(s / 60);
    const r = Math.floor(s % 60);
    return `${m}:${r < 10 ? "0" + r : r}`;
  };
  const pct = dur > 0 ? Math.min(100, (cur / dur) * 100) : 0;
  const seek = (e) => {
    const a = audioRef.current;
    if (!a || !dur) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (("touches" in e) ? e.touches[0].clientX : e.clientX) - rect.left;
    a.currentTime = Math.max(0, Math.min(dur, (x / rect.width) * dur));
  };
  return (
    <div
      className={`inline-flex items-center gap-3 rounded-full ${compact ? "px-2 py-1" : "px-3 py-1.5"}`}
      style={{
        background: "rgba(215,187,122,0.06)",
        border: `1px solid ${TOK.goldMist}`,
        maxWidth: compact ? "240px" : "260px",
      }}
      data-testid={testId}
    >
      <button
        onClick={toggle}
        aria-label={playing ? "Pause" : "Play"}
        className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-transform active:scale-95"
        style={{
          background: playing ? TOK.gold : "rgba(215,187,122,0.22)",
          border: `1px solid ${TOK.gold}`,
          color: playing ? TOK.ink : TOK.gold,
        }}
      >
        {playing ? <Pause size={13} fill="currentColor" /> : <Play size={13} fill="currentColor" />}
      </button>
      <div
        onClick={seek}
        onTouchStart={seek}
        className="flex-1 h-[3px] rounded-full cursor-pointer relative"
        style={{ background: "rgba(215,187,122,0.20)" }}
      >
        <div
          className="absolute inset-y-0 left-0 rounded-full"
          style={{ width: `${pct}%`, background: TOK.gold, boxShadow: "0 0 6px rgba(215,187,122,0.55)" }}
        />
      </div>
      <span
        className="shrink-0 text-[0.62rem] tabular-nums"
        style={{ color: TOK.creamDim, fontFamily: '"Manrope", sans-serif', minWidth: 32, textAlign: "right" }}
      >
        {fmt(cur)}
      </span>
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setCur(0); }}
        onTimeUpdate={(e) => { if (scanned) setCur(e.currentTarget.currentTime); }}
        onLoadedMetadata={(e) => applyDuration(e.currentTarget)}
        onDurationChange={(e) => { if (isFinite(e.currentTarget.duration)) setDur(e.currentTarget.duration || 0); }}
      />
    </div>
  );
}

const CurationStep = React.forwardRef(function CurationStep({ themeId, durationMin, customConcern, onScriptChange, initialScript, seedKey }, ref) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [script, setScript] = useState(null);   // { sec: [text, ...] }
  // text -> bool (kept). Default all kept. We key by section+text so
  // identical lines in different sections don't collide.
  const [kept, setKept] = useState({});         // { "sec::text": true }
  // text -> { id, url } once uploaded
  const [voiceByLine, setVoiceByLine] = useState({}); // { "sec::text": {id, url} }

  // If the wizard already handed us a seed script (from an
  // Installation Library collection), use those exact lines and
  // skip the preview-script fetch — otherwise the collection's
  // curated words would be discarded and replaced by a generic
  // theme preview.
  const _seedKeyRef = React.useRef(null);
  useEffect(() => {
    if (!initialScript) return;
    if (_seedKeyRef.current === seedKey) return; // already seeded
    _seedKeyRef.current = seedKey;
    // Normalise: accept both `{sec: ["text", ...]}` and the API
    // shape `{sec: [{text, voice_clip_id?}, ...]}`. Preserve any
    // voice_clip_id — critical for draft restores where the user
    // already recorded lines before the wizard was reopened.
    const norm = {};
    const seededVoice = {};
    for (const [sec, arr] of Object.entries(initialScript)) {
      const cleaned = [];
      (arr || []).forEach((l) => {
        const text = typeof l === "string" ? l : (l && l.text) || "";
        if (!text) return;
        cleaned.push(text);
        if (l && typeof l === "object" && l.voice_clip_id && l.voice_clip_url !== null) {
          seededVoice[`${sec}::${text}`] = {
            id: l.voice_clip_id,
            url: l.voice_clip_url || `/api/audio-journeys/voice-clips/${l.voice_clip_id}/audio`,
          };
        }
      });
      norm[sec] = cleaned;
    }
    setScript(norm);
    const initKept = {};
    for (const [sec, lines] of Object.entries(norm)) {
      lines.forEach((l) => { initKept[`${sec}::${l}`] = true; });
    }
    setKept(initKept);
    setVoiceByLine(seededVoice);
    setLoading(false);
  }, [initialScript, seedKey]);

  // Fetch the preview-script ONCE per (theme, duration). If the user
  // navigates back and edits the theme, we re-fetch so curation
  // reflects the current selection. Skipped when a seed script is
  // in play — the collection lines are already loaded.
  useEffect(() => {
    if (!themeId) { setLoading(false); return; }
    if (_seedKeyRef.current) return; // seeded by a collection — don't overwrite
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios.post(`${API}/api/audio-journeys/preview-script`,
      { affirmation_theme: themeId, duration_minutes: durationMin, custom_concern: customConcern || null },
      auth(),
    ).then((r) => {
      if (cancelled) return;
      const s = r.data?.script || {};
      setScript(s);
      // mark all lines kept by default
      const initKept = {};
      Object.entries(s).forEach(([sec, lines]) => {
        (lines || []).forEach((l) => { initKept[`${sec}::${l}`] = true; });
      });
      setKept(initKept);
      setVoiceByLine({});
    }).catch((e) => {
      if (cancelled) return;
      console.error("[curation] preview failed:", e);
      setError(e?.response?.data?.detail || "Couldn't load your script. Tap back and try again.");
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [themeId, durationMin, customConcern]);

  // Build and emit the approved_script whenever state changes.
  useEffect(() => {
    if (!script) return;
    const approved = {};
    Object.entries(script).forEach(([sec, lines]) => {
      approved[sec] = (lines || [])
        .filter((l) => kept[`${sec}::${l}`])
        .map((text) => {
          const v = voiceByLine[`${sec}::${text}`];
          return v ? { text, voice_clip_id: v.id } : { text };
        });
    });
    onScriptChange(approved);
  }, [script, kept, voiceByLine, onScriptChange]);

  // Append a user-typed line into a section. The line behaves
  // identically to LLM-generated lines from this point on — it can
  // be kept/skipped, voiced, and is included in approved_script.
  const addLine = React.useCallback((sec, text) => {
    const t = (text || "").trim();
    if (!t) return;
    setScript((p) => {
      if (!p) return p;
      const cur = p[sec] || [];
      if (cur.includes(t)) return p; // de-dupe
      return { ...p, [sec]: [...cur, t] };
    });
    setKept((p) => ({ ...p, [`${sec}::${t}`]: true }));
  }, []);

  // Fully remove a line from the section — different from
  // "skip" which keeps the line visible but toggled off. This
  // is what the user reaches for when a line simply doesn't
  // belong on their installation.
  const removeLine = React.useCallback((sec, text) => {
    setScript((p) => {
      if (!p) return p;
      const cur = p[sec] || [];
      return { ...p, [sec]: cur.filter((x) => x !== text) };
    });
    setKept((p) => {
      const n = { ...p };
      delete n[`${sec}::${text}`];
      return n;
    });
    setVoiceByLine((p) => {
      const n = { ...p };
      delete n[`${sec}::${text}`];
      return n;
    });
  }, []);

  // Expose a merge callback via ref so the "Explore the Full
  // Affirmation Library" panel can inject a whole collection's
  // lines into this step's live line set — without which the added
  // lines would only land on the parent's approvedScript and be
  // clobbered on the next auto-emit.
  React.useImperativeHandle(ref, () => ({
    mergeExternalLines: (bySection) => {
      if (!bySection || typeof bySection !== "object") return;
      for (const [sec, arr] of Object.entries(bySection)) {
        if (!Array.isArray(arr)) continue;
        for (const item of arr) {
          const text = typeof item === "string" ? item : (item && item.text) || "";
          if (text) addLine(sec, text);
        }
      }
    },
  }), [addLine]);

  // Cost-strip running totals. Once we have a script we ask the
  // backend which of the currently-kept lines are already in the
  // sha1 TTS cache — that lets us show an honest "$X est. cost" and
  // celebrate the user when they record their own lines (those lines
  // skip TTS entirely → $0). Debounced: only refetch when the set of
  // kept lines actually changes.
  const [costInfo, setCostInfo] = useState({ items: [], loaded: false });
  const keptLinesKey = React.useMemo(() => {
    if (!script) return "";
    return Object.entries(script)
      .flatMap(([sec, ls]) => ls.filter((l) => kept[`${sec}::${l}`] !== false).map((l) => `${sec}::${l}`))
      .sort()
      .join("|");
  }, [script, kept]);
  useEffect(() => {
    if (!script) return;
    const lines = [];
    Object.entries(script).forEach(([sec, ls]) => {
      ls.forEach((l) => { if (kept[`${sec}::${l}`] !== false) lines.push(l); });
    });
    if (!lines.length) {
      setCostInfo({ items: [], loaded: true });
      return;
    }
    let cancelled = false;
    axios.post(`${API}/api/audio-journeys/tts-line-status`, { lines }, auth())
      .then((r) => { if (!cancelled) setCostInfo({ items: r.data?.items || [], loaded: true }); })
      .catch(() => { if (!cancelled) setCostInfo({ items: [], loaded: true }); });
    return () => { cancelled = true; };
  }, [keptLinesKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="mt-8 flex items-center gap-3 text-sm italic" style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif' }}>
        <Loader2 size={18} className="animate-spin" /> Composing your script…
      </div>
    );
  }
  if (error) {
    return <p className="mt-6 text-sm italic" style={{ color: "#dc8b6c" }}>{error}</p>;
  }
  if (!script) return null;

  const keptCount = Object.values(kept).filter(Boolean).length;
  const recordedCount = Object.keys(voiceByLine).length;
  // Cost math:
  //   • A line with a user voice clip → $0 TTS
  //   • A cached line (already in the sha1 TTS cache) → $0
  //   • Otherwise → ~$0.012 per line at ElevenLabs avg pricing
  //     (50 chars ≈ $0.012 with the v2.5 turbo model, well under $0.03 per long line)
  const COST_PER_FRESH_LINE = 0.012;
  const cachedSet = new Set(costInfo.items.filter((i) => i.cached).map((i) => i.text));
  const recordedLineTextSet = new Set(
    Object.entries(voiceByLine).map(([key]) => key.split("::").slice(1).join("::"))
  );
  let freshCount = 0;
  let cachedCount = 0;
  Object.entries(script).forEach(([sec, ls]) => {
    ls.forEach((l) => {
      if (kept[`${sec}::${l}`] === false) return;
      if (recordedLineTextSet.has(l)) return;
      if (cachedSet.has(l)) cachedCount += 1;
      else freshCount += 1;
    });
  });
  const estCost = (freshCount * COST_PER_FRESH_LINE).toFixed(2);
  const estRenderSec = Math.max(20, Math.round(freshCount * 2.2 + 18)); // rough TTS+mix wallclock

  return (
    <div className="mt-5" data-testid="journey-curation">
      <div className="mb-5 px-4 py-3 rounded-xl" style={{ background: "rgba(215,187,122,0.06)", border: `1px solid ${TOK.goldMist}` }}>
        <p className="text-xs italic leading-relaxed" style={{ color: TOK.cream, fontFamily: '"Cormorant Garamond", serif' }}>
          Read every line. <span style={{ color: TOK.gold }}>Tap the line</span> to keep or skip. Tap the <span style={{ color: TOK.gold }}>microphone</span> to speak it in your own voice — your subconscious knows the sound of you better than any other voice in the world.
        </p>
        <div className="mt-2.5 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="curation-cost-strip">
          <CostCell label="Your voice" value={recordedCount} accent={TOK.gold} />
          <CostCell label="Soulumo voice" value={keptCount - recordedCount} accent={TOK.goldSoft} />
          <CostCell
            label="Ready in"
            value={estRenderSec < 60 ? "under a minute" : `about ${Math.round(estRenderSec / 60)} min`}
            accent={TOK.goldSoft}
            hint={cachedCount > 0 ? `${cachedCount} already ready` : null}
          />
          {/* "Estimated spend" removed from user UI — visible in admin only. */}
        </div>
      </div>
      {SECTION_FLOW.map((s) => (
        <CurationSection
          key={s.id}
          section={s}
          lines={script[s.id] || []}
          kept={kept}
          setKept={setKept}
          voiceByLine={voiceByLine}
          setVoiceByLine={setVoiceByLine}
          onAddLine={addLine}
          onRemoveLine={removeLine}
        />
      ))}
    </div>
  );
});

function CurationSection({ section, lines, kept, setKept, voiceByLine, setVoiceByLine, onAddLine, onRemoveLine }) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const inputRef = useRef(null);

  const commit = () => {
    const t = draft.trim();
    if (t.length < 2) return;
    onAddLine(section.id, t);
    setDraft("");
    setAdding(false);
  };

  return (
    <div className="mb-9" data-testid={`curation-section-${section.id}`}>
      <p className="uppercase tracking-[0.28em] text-[0.6rem] mb-1" style={{ color: TOK.gold, fontFamily: '"Manrope", sans-serif' }}>{section.label}</p>
      <p className="italic text-xs mb-3" style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif' }}>{section.blurb}</p>
      <div className="border-t" style={{ borderColor: "rgba(215,187,122,0.16)" }}>
        {lines.map((text, idx) => (
          <LineCard
            key={`${section.id}-${idx}-${text}`}
            sectionId={section.id}
            text={text}
            isKept={kept[`${section.id}::${text}`] !== false}
            onToggleKept={() => setKept((p) => ({ ...p, [`${section.id}::${text}`]: !(p[`${section.id}::${text}`] !== false) }))}
            onRemove={() => onRemoveLine && onRemoveLine(section.id, text)}
            voice={voiceByLine[`${section.id}::${text}`] || null}
            onRecorded={(clip) => setVoiceByLine((p) => ({ ...p, [`${section.id}::${text}`]: clip }))}
            onClearVoice={() => setVoiceByLine((p) => { const n = { ...p }; delete n[`${section.id}::${text}`]; return n; })}
            testIndex={idx}
          />
        ))}
      </div>
      {adding ? (
        <div
          className="mt-2 px-3 py-2 rounded-lg"
          style={{ background: "rgba(215,187,122,0.06)", border: `1px solid ${TOK.gold}` }}
          data-testid={`curation-add-form-${section.id}`}
        >
          <textarea
            ref={inputRef}
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) commit();
              if (e.key === "Escape") { setAdding(false); setDraft(""); }
            }}
            placeholder={`A ${section.label.toLowerCase()} line in your own words…`}
            rows={2}
            maxLength={240}
            className="w-full bg-transparent outline-none resize-none text-sm"
            style={{
              color: TOK.cream,
              fontFamily: '"Cormorant Garamond", serif',
              fontSize: "1.02rem",
              caretColor: TOK.gold,
            }}
            data-testid={`curation-add-input-${section.id}`}
          />
          <div className="mt-2 flex items-center gap-2 justify-end">
            <span className="text-[0.6rem]" style={{ color: TOK.creamDim, fontFamily: '"Manrope", sans-serif' }}>
              {draft.trim().length}/240
            </span>
            <button
              onClick={() => { setAdding(false); setDraft(""); }}
              className="text-[0.6rem] uppercase tracking-[0.22em] px-3 py-1"
              style={{ color: TOK.creamDim, fontFamily: '"Manrope", sans-serif' }}
              data-testid={`curation-add-cancel-${section.id}`}
            >
              Cancel
            </button>
            <button
              onClick={commit}
              disabled={draft.trim().length < 2}
              className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[0.6rem] uppercase tracking-[0.22em]"
              style={{
                background: draft.trim().length >= 2 ? "rgba(215,187,122,0.22)" : "rgba(215,187,122,0.06)",
                border: `1px solid ${draft.trim().length >= 2 ? TOK.gold : TOK.goldMist}`,
                color: TOK.cream,
                fontFamily: '"Manrope", sans-serif',
                opacity: draft.trim().length >= 2 ? 1 : 0.5,
              }}
              data-testid={`curation-add-save-${section.id}`}
            >
              <Check size={11} /> Add
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setAdding(true)}
          data-testid={`curation-add-toggle-${section.id}`}
          className="mt-3 inline-flex items-center gap-1.5 text-[0.6rem] uppercase tracking-[0.22em] py-1.5 opacity-80 hover:opacity-100 transition-opacity"
          style={{
            background: "transparent",
            color: TOK.goldSoft,
            fontFamily: '"Manrope", sans-serif',
          }}
        >
          <Plus size={11} /> Add your own line
        </button>
      )}
    </div>
  );
}

function LineCard({ sectionId, text, isKept, onToggleKept, onRemove, voice, onRecorded, onClearVoice, testIndex }) {
  // recording = "idle" | "recording" | "preview" | "uploading"
  const [recState, setRecState] = useState("idle");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [blob, setBlob] = useState(null);
  const [error, setError] = useState(null);
  const [micErr, setMicErr] = useState(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const start = async () => {
    setError(null); setMicErr(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream);
      mediaRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const b = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        setBlob(b);
        setPreviewUrl(URL.createObjectURL(b));
        setRecState("preview");
        try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch (_) {}
      };
      mr.start();
      setRecState("recording");
    } catch (e) {
      console.error("[record] getUserMedia failed:", e);
      setMicErr(e?.name === "NotFoundError" ? "nomic" : "blocked");
      setRecState("idle");
    }
  };

  const stop = () => { try { mediaRef.current?.stop(); } catch (_) {} };

  const reset = () => {
    if (previewUrl) try { URL.revokeObjectURL(previewUrl); } catch (_) {}
    setPreviewUrl(null);
    setBlob(null);
    setRecState("idle");
  };

  const save = async () => {
    if (!blob) return;
    setRecState("uploading");
    setError(null);
    try {
      const fd = new FormData();
      // Pick the extension from the actual MIME the browser produced.
      // iOS Safari's MediaRecorder produces audio/mp4 (AAC in an MP4
      // container) — Android/desktop Chromium produce audio/webm.
      // iter265 scar: forcing every blob to `.webm` mislabeled iOS
      // uploads, then the audio element refused to play them.
      const t = (blob.type || "").toLowerCase();
      let ext = "webm";
      if (t.includes("mp4") || t.includes("aac") || t.includes("m4a")) ext = "m4a";
      else if (t.includes("ogg")) ext = "ogg";
      else if (t.includes("wav")) ext = "wav";
      fd.append("file", blob, `voice.${ext}`);
      fd.append("line_text", text);
      fd.append("section", sectionId);
      const r = await axios.post(`${API}/api/audio-journeys/voice-clips`, fd, {
        headers: { ...auth().headers, "Content-Type": "multipart/form-data" },
      });
      onRecorded({ id: r.data.id, url: r.data.url, duration: r.data.duration_seconds });
      // Warm 3-second auto-preview so members hear themselves back
      // immediately — the confirmation the recording is really them,
      // in their own voice, before they move on.
      try {
        const previewSrc = r.data.url?.startsWith("http") ? r.data.url : `${API}${r.data.url}`;
        const a = new Audio(previewSrc);
        a.volume = 0.9;
        await a.play();
        // Cap the preview at 3s so it doesn't play a whole long clip.
        setTimeout(() => { try { a.pause(); } catch (_) { /* ignore */ } }, 3000);
      } catch (_e) {
        // Browser blocked autoplay — silently skip the preview.
        // The manual play control is right there.
      }
      reset();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : (e?.message || "Couldn't save the recording."));
      setRecState("preview");
    }
  };

  const removeRecorded = async () => {
    if (!voice?.id) return;
    try {
      await axios.delete(`${API}/api/audio-journeys/voice-clips/${voice.id}`, auth());
    } catch (_) {}
    onClearVoice();
  };

  const skipped = !isKept;

  return (
    <div
      className="py-3.5 pl-3 pr-1 border-b transition-opacity duration-200"
      style={{
        borderColor: "rgba(215,187,122,0.16)",
        boxShadow: voice ? `inset 2px 0 0 ${TOK.gold}` : "none",
        opacity: skipped ? 0.45 : 1,
      }}
      data-testid={`curation-line-${sectionId}-${testIndex}`}
    >
      <div className="flex items-start gap-3">
        <button
          onClick={onToggleKept}
          data-testid={`curation-keep-${sectionId}-${testIndex}`}
          aria-label={isKept ? "Skip this line" : "Keep this line"}
          className="shrink-0 w-[18px] h-[18px] rounded-full inline-flex items-center justify-center mt-[5px] transition-colors duration-200"
          style={{
            background: isKept ? TOK.gold : "transparent",
            border: `1px solid ${isKept ? TOK.gold : TOK.goldMist}`,
          }}
        >
          {isKept && <Check size={10} color="#0d1f3a" strokeWidth={3} />}
        </button>
        <p
          className="flex-1 leading-snug"
          style={{
            color: TOK.cream,
            fontFamily: '"Cormorant Garamond", serif',
            fontSize: "1.14rem",
            fontWeight: 400,
            textDecoration: skipped ? "line-through" : "none",
          }}
        >
          {text}
        </p>
      </div>

      <div className="mt-2 ml-[30px] flex flex-wrap items-center gap-x-5 gap-y-2" style={{ fontFamily: '"Manrope", sans-serif' }}>
        <ReadAloud text={text} size={12} tint={TOK.gold} testId={`curation-readaloud-${sectionId}-${testIndex}`} />
        <button
          onClick={() => {
            if (recState === "recording") return stop();
            if (recState === "preview" || recState === "uploading") return;
            if (voice) return;
            start();
          }}
          disabled={recState === "uploading"}
          data-testid={`curation-mic-${sectionId}-${testIndex}`}
          aria-label={recState === "recording" ? "Stop recording" : "Record your voice"}
          className="inline-flex items-center gap-1.5 transition-opacity duration-200 hover:opacity-100"
          style={{
            color: recState === "recording" ? "#E07A6A" : voice ? TOK.gold : TOK.goldSoft,
            opacity: recState === "recording" ? 1 : 0.85,
            fontSize: "0.6rem",
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            animation: recState === "recording" ? "recordPulse 1.4s ease-in-out infinite" : "none",
          }}
          title={voice ? "Remove the saved recording to re-record" : (recState === "recording" ? "Tap to stop" : "Tap to record")}
        >
          {recState === "uploading" ? <Loader2 size={12} className="animate-spin" />
            : recState === "recording" ? <span className="block" style={{ width: 8, height: 8, background: "#E07A6A", borderRadius: 2 }} />
            : voice ? <Check size={12} /> : <Mic size={12} />}
          {recState === "recording" ? "Stop" : voice ? "Your voice" : "Record"}
        </button>
        {voice && (
          <button
            onClick={onClearVoice}
            data-testid={`curation-voice-remove-${sectionId}-${testIndex}`}
            className="text-[0.6rem] uppercase tracking-[0.2em] opacity-70 hover:opacity-100 transition-opacity"
            style={{ color: TOK.creamDim }}
          >
            Re-record
          </button>
        )}
        {onRemove && (
          <button
            onClick={onRemove}
            data-testid={`curation-line-delete-${sectionId}-${testIndex}`}
            aria-label="Delete this line"
            title="Remove this line entirely"
            className="ml-auto inline-flex items-center justify-center w-7 h-7 opacity-40 hover:opacity-90 transition-opacity"
            style={{ color: TOK.creamDim }}
          >
            <Trash2 size={13} />
          </button>
        )}
      </div>
      {voice && (
        <div className="mt-2 ml-[30px]" data-testid={`curation-voice-${sectionId}-${testIndex}`}>
          <GoldPlayer src={`${API}${voice.url}`} testId={`curation-voice-player-${sectionId}-${testIndex}`} compact />
        </div>
      )}
      {recState === "preview" && previewUrl && (
        <div className="mt-2 ml-7 flex items-center gap-2 flex-wrap">
          <GoldPlayer src={previewUrl} testId={`curation-preview-player-${sectionId}-${testIndex}`} compact />
          <button
            onClick={save}
            data-testid={`curation-save-${sectionId}-${testIndex}`}
            className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[0.6rem] uppercase tracking-[0.22em]"
            style={{ background: "rgba(215,187,122,0.22)", border: `1px solid ${TOK.gold}`, color: TOK.cream, fontFamily: '"Manrope", sans-serif' }}
          >
            <Check size={11} /> Save
          </button>
          <button
            onClick={reset}
            data-testid={`curation-rerecord-${sectionId}-${testIndex}`}
            className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[0.6rem] uppercase tracking-[0.22em]"
            style={{ background: "transparent", border: `1px solid ${TOK.goldMist}`, color: TOK.creamDim, fontFamily: '"Manrope", sans-serif' }}
          >
            <RotateCcw size={11} /> Re-record
          </button>
        </div>
      )}
      {error && <p className="text-[0.6rem] italic mt-1 ml-7" style={{ color: "#dc8b6c" }}>{error}</p>}
      {micErr && <div className="mt-2"><MicHelp kind={micErr} onRetry={start} /></div>}
    </div>
  );
}

// ── My Voice library ─────────────────────────────────────────────
// A private, full-screen drawer of every voice clip the user has ever
// recorded for any journey. Grouped by Reclaim Installation section.
// Plays back, lets them remove — never leaves their account. Designed
// for a grounding moment between full journeys: a place to revisit
// just their own voice.
export function VoiceLibrary({ onClose }) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/audio-journeys/voice-clips`, auth());
      setItems(r.data?.items || []);
      setError(null);
    } catch (e) {
      console.error("[voice-lib] list failed:", e);
      setError("Couldn't load your voice library.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/api/audio-journeys/voice-clips/${id}`, auth());
      setItems((p) => p.filter((c) => c.id !== id));
    } catch (e) {
      console.error("[voice-lib] delete failed:", e);
    }
  };

  // Group clips by section, preserving SECTION_FLOW order.
  const bySection = SECTION_FLOW.map((s) => ({
    ...s,
    clips: items.filter((c) => c.section === s.id),
  }));
  const total = items.length;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      style={{ background: TOK.pageBg }}
      data-testid="voice-library-modal"
    >
      <div className="max-w-2xl mx-auto px-5 sm:px-8 pt-10 pb-24">
        <div className="flex items-baseline justify-between mb-6">
          <p className="uppercase tracking-[0.4em] text-[0.55rem]" style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}>
            Reclaim · Private
          </p>
          <button
            onClick={onClose}
            data-testid="voice-library-close"
            aria-label="Close voice library"
            style={{ color: TOK.creamDim }}
          >
            <X size={20} />
          </button>
        </div>

        <h1 className="font-light leading-[1.05]" style={{ fontFamily: '"Cormorant Garamond", serif', fontSize: "2.4rem", color: TOK.cream }}>
          My Voice
        </h1>
        <p className="mt-3 italic max-w-md leading-relaxed" style={{ fontFamily: '"Cormorant Garamond", serif', color: TOK.creamDim, fontSize: "1.02rem" }}>
          Every line you have spoken in your own voice. Listen back, return to yourself between journeys. None of this is shared. None of this leaves you.
        </p>

        {loading && (
          <div className="mt-10 flex items-center gap-2 text-sm italic" style={{ color: TOK.creamDim }}>
            <Loader2 size={16} className="animate-spin" /> Gathering your recordings…
          </div>
        )}
        {error && <p className="mt-8 text-sm italic" style={{ color: "#dc8b6c" }}>{error}</p>}

        {!loading && !error && total === 0 && (
          <div className="mt-12 px-6 py-10 rounded-2xl text-center" style={{ background: "rgba(215,187,122,0.05)", border: `1px solid ${TOK.goldMist}` }} data-testid="voice-library-empty">
            <Mic size={28} className="mx-auto" style={{ color: TOK.goldSoft }} />
            <p className="mt-4 italic" style={{ fontFamily: '"Cormorant Garamond", serif', color: TOK.cream, fontSize: "1.08rem" }}>
              You haven&apos;t recorded a line yet.
            </p>
            <p className="mt-2 text-xs italic max-w-sm mx-auto leading-relaxed" style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif' }}>
              Build a journey and tap the microphone on any line you are ready to speak in your own voice. Your subconscious knows the sound of you better than any other voice in the world.
            </p>
          </div>
        )}

        {!loading && !error && total > 0 && (
          <div className="mt-8" data-testid="voice-library-list">
            <p className="text-[0.6rem] uppercase tracking-[0.22em] mb-6" style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}>
              {total} recording{total === 1 ? "" : "s"} · across {bySection.filter((s) => s.clips.length).length} of 6 sections
            </p>
            {bySection.map((s) => {
              if (!s.clips.length) return null;
              return (
                <div key={s.id} className="mb-8" data-testid={`voice-library-section-${s.id}`}>
                  <p className="uppercase tracking-[0.28em] text-[0.6rem] mb-1" style={{ color: TOK.gold, fontFamily: '"Manrope", sans-serif' }}>{s.label}</p>
                  <p className="italic text-xs mb-3" style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif' }}>{s.blurb}</p>
                  <div className="space-y-2">
                    {s.clips.map((c) => (
                      <VoiceLibraryRow key={c.id} clip={c} onRemove={() => remove(c.id)} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function VoiceLibraryRow({ clip, onRemove }) {
  const [confirming, setConfirming] = useState(false);
  return (
    <div
      className="px-3 py-2.5 rounded-lg"
      style={{ background: "rgba(215,187,122,0.05)", border: `1px solid ${TOK.goldMist}` }}
      data-testid={`voice-library-clip-${clip.id}`}
    >
      <p className="text-sm leading-relaxed" style={{ color: TOK.cream, fontFamily: '"Cormorant Garamond", serif', fontSize: "1.02rem" }}>
        {clip.line_text}
      </p>
      <div className="mt-2 flex items-center gap-3 flex-wrap">
        <GoldPlayer
          src={`${API}${clip.url}`}
          testId={`voice-library-audio-${clip.id}`}
          compact
        />
        {clip.duration_seconds > 0 && (
          <span className="text-[0.6rem]" style={{ color: TOK.creamDim, fontFamily: '"Manrope", sans-serif' }}>
            {clip.duration_seconds.toFixed(1)}s
          </span>
        )}
        <div className="ml-auto">
          {confirming ? (
            <div className="inline-flex items-center gap-2">
              <button
                onClick={onRemove}
                data-testid={`voice-library-confirm-remove-${clip.id}`}
                className="text-[0.6rem] uppercase tracking-[0.22em] px-2.5 py-1 rounded-full"
                style={{ background: "rgba(220,139,108,0.18)", border: "1px solid #dc8b6c", color: "#dc8b6c", fontFamily: '"Manrope", sans-serif' }}
              >
                Remove
              </button>
              <button
                onClick={() => setConfirming(false)}
                className="text-[0.6rem] uppercase tracking-[0.22em]"
                style={{ color: TOK.creamDim, fontFamily: '"Manrope", sans-serif' }}
              >
                Keep
              </button>
            </div>
          ) : (
            <button
              onClick={() => setConfirming(true)}
              data-testid={`voice-library-remove-${clip.id}`}
              className="text-[0.6rem] uppercase tracking-[0.22em]"
              style={{ color: TOK.creamDim, fontFamily: '"Manrope", sans-serif' }}
            >
              Remove
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Signature Blueprints row (on /my-journeys) ───────────────────
// A horizontal scroll of 10 curated templates. Two flows:
//   • TAP a card → one-tap clone (renders immediately in background)
//   • TAP the small "tweak" pill → opens the wizard pre-filled with
//     the blueprint as a preset (user can change anything before
//     creating). Cards the user has already cloned show a subtle
//     "✓ In your library" badge.
function SignatureBlueprintsRow({ onCloned, onPickAsPreset }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cloningId, setCloningId] = useState(null);

  const load = React.useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/audio-journeys/blueprints`, auth());
      setItems(r.data?.items || []);
    } catch (e) {
      console.error("[blueprints] list failed:", e);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const clone = async (bp) => {
    setCloningId(bp.id);
    try {
      const r = await axios.post(`${API}/api/audio-journeys/blueprints/${bp.id}/clone`, {}, auth());
      onCloned(r.data);
      // Refresh in_library badge.
      setItems((p) => p.map((b) => b.id === bp.id ? { ...b, in_library: true } : b));
    } catch (e) {
      console.error("[blueprints] clone failed:", e);
    } finally {
      setCloningId(null);
    }
  };

  if (loading || !items.length) return null;

  return (
    <div className="mb-10" data-testid="signature-blueprints-row">
      <div className="mb-3 flex items-baseline justify-between">
        <p className="uppercase tracking-[0.28em] text-[0.6rem]" style={{ color: TOK.gold, fontFamily: '"Manrope", sans-serif' }}>
          Signature Installations
        </p>
        <p className="italic text-xs" style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif' }}>
          One tap. Ready when you are.
        </p>
      </div>
      <div className="-mx-2 px-2 overflow-x-auto" style={{ scrollbarWidth: "thin" }}>
        <div className="flex gap-3" style={{ minWidth: "min-content" }}>
          {items.map((bp) => (
            <BlueprintCard
              key={bp.id}
              bp={bp}
              cloning={cloningId === bp.id}
              onClone={() => clone(bp)}
              onTweak={() => onPickAsPreset(bp)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function BlueprintCard({ bp, cloning, onClone, onTweak }) {
  return (
    <div
      className="shrink-0 rounded-2xl p-4 flex flex-col relative"
      style={{
        width: "200px",
        minHeight: "188px",
        background: "rgba(215,187,122,0.05)",
        border: `1px solid ${bp.in_library ? bp.accent : TOK.goldMist}`,
      }}
      data-testid={`blueprint-card-${bp.id}`}
    >
      <div className="flex items-baseline justify-between">
        <p className="uppercase tracking-[0.22em] text-[0.55rem]" style={{ color: bp.accent, fontFamily: '"Manrope", sans-serif' }}>
          {bp.group}
        </p>
        {bp.in_library && (
          <span className="text-[0.5rem] uppercase tracking-[0.22em]" style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }} title="Already in your library">
            ✓ Saved
          </span>
        )}
      </div>
      <p className="mt-2 font-light leading-tight" style={{ fontFamily: '"Cormorant Garamond", serif', fontSize: "1.25rem", color: TOK.cream }}>
        {bp.label}
      </p>
      <p className="mt-1.5 italic text-xs flex-1" style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif', lineHeight: 1.4 }}>
        {bp.blurb}
      </p>
      <div className="mt-3 flex items-center gap-2 justify-between">
        <p className="text-[0.55rem]" style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}>
          {bp.duration_minutes} min
        </p>
        <div className="flex items-center gap-1.5">
          <button
            onClick={onTweak}
            data-testid={`blueprint-tweak-${bp.id}`}
            className="text-[0.55rem] uppercase tracking-[0.22em] px-2 inline-flex items-center"
            style={{ color: TOK.creamDim, fontFamily: '"Manrope", sans-serif', minHeight: 44 }}
            title="Open in wizard to customise before creating"
          >
            Tweak
          </button>
          <button
            onClick={onClone}
            disabled={cloning}
            data-testid={`blueprint-clone-${bp.id}`}
            className="inline-flex items-center gap-1 text-[0.55rem] uppercase tracking-[0.22em] px-3 rounded-full"
            style={{
              background: bp.accent + "22",
              border: `1px solid ${bp.accent}`,
              color: TOK.cream,
              fontFamily: '"Manrope", sans-serif',
              opacity: cloning ? 0.6 : 1,
              minHeight: 44,
            }}
          >
            {cloning ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />}
            {cloning ? "Adding…" : "Add"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Resume Banner ────────────────────────────────────────────────
// Surfaced when a server-side draft exists at wizard open. Quiet,
// ceremonial. Three paths:
//   • Resume — re-enter the wizard exactly where they left off
//   • Build now — skip the rest of the wizard, render with what
//     they already have (defaults fill anything missing). Only
//     shown when the draft is far enough along to make this useful
//     (i.e. at minimum a theme is picked).
//   • Start fresh — discard the draft and begin clean.
function ResumeBanner({ draft, onResume, onDiscard, onBuildNow, submitting }) {
  const stepName = ["Reflection", "Atmosphere", "Resonance", "Frequency", "Voice"][draft.step] || "your journey";
  let when = "";
  try {
    const d = new Date(draft.updated_at);
    const diffMin = Math.round((new Date() - d) / 60000);
    if (diffMin < 2) when = "moments ago";
    else if (diffMin < 60) when = `${diffMin} minutes ago`;
    else if (diffMin < 24 * 60) when = `${Math.round(diffMin / 60)} hours ago`;
    else when = d.toLocaleDateString(undefined, { weekday: "long", hour: "numeric", minute: "2-digit" });
  } catch (_) { when = ""; }
  const s = draft.state || {};
  const intent = s.name?.trim() || (s.affirmation_theme ? `a ${s.affirmation_theme.replace(/_/g, " ")} journey` : "a journey");
  return (
    <div className="mb-8 px-5 py-4 rounded-2xl" style={{ background: "rgba(215,187,122,0.08)", border: `1px solid ${TOK.gold}` }} data-testid="journey-resume-banner">
      <p className="text-[0.6rem] uppercase tracking-[0.28em]" style={{ color: TOK.gold, fontFamily: '"Manrope", sans-serif' }}>
        Continue where you left off
      </p>
      <p className="mt-2 italic leading-relaxed" style={{ color: TOK.cream, fontFamily: '"Cormorant Garamond", serif', fontSize: "1.05rem" }}>
        You were building <span style={{ color: TOK.gold }}>{intent}</span>{when && <> — paused at <span style={{ color: TOK.gold }}>{stepName}</span>, {when}</>}.
      </p>
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <button onClick={onResume} disabled={submitting} data-testid="journey-resume-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full"
          style={{ background: "rgba(215,187,122,0.22)", border: `1px solid ${TOK.gold}`, color: TOK.cream, fontFamily: '"Manrope", sans-serif', fontSize: "0.7rem", letterSpacing: "0.22em", textTransform: "uppercase", opacity: submitting ? 0.5 : 1 }}>
          <RotateCcw size={12} /> Resume
        </button>
        {onBuildNow && (
          <button
            onClick={onBuildNow}
            disabled={submitting}
            data-testid="journey-buildnow-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full"
            style={{ background: "rgba(215,187,122,0.10)", border: `1px solid ${TOK.gold}`, color: TOK.cream, fontFamily: '"Manrope", sans-serif', fontSize: "0.7rem", letterSpacing: "0.22em", textTransform: "uppercase", opacity: submitting ? 0.5 : 1 }}
            title="Render with what you have. Anything you did not fill in is set with calm defaults."
          >
            {submitting ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
            {submitting ? "Building…" : "Build now"}
          </button>
        )}
        <button onClick={onDiscard} disabled={submitting} data-testid="journey-resume-discard-btn" className="text-[0.65rem] uppercase tracking-[0.22em] px-3 py-2"
          style={{ color: TOK.creamDim, fontFamily: '"Manrope", sans-serif', opacity: submitting ? 0.5 : 1 }}>
          Start fresh
        </button>
      </div>
      {onBuildNow && (
        <p className="mt-3 italic text-[0.7rem] leading-snug" style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif' }}>
          Tap <span style={{ color: TOK.goldSoft }}>Build now</span> to render with what you already chose. Anything you didn&apos;t reach gets a calm default — you can refine the journey afterwards.
        </p>
      )}
    </div>
  );
}

// ── Opening Ritual Screen ────────────────────────────────────────
// First impression of the Life Design Ritual. Spacious, intentional,
// luxury-without-flash. Holds the user in a single emotional moment
// before any selections begin. Audio intro placeholder will be wired
// to a Michael-recorded MP3 once delivered. Jun 10 2026.
function OpeningRitualScreen({ onBegin }) {
  return (
    <div className="pt-8 pb-4" data-testid="journey-opening-ritual">
      <div className="text-center mb-6">
        <SoulumoWord size="xs" />
      </div>
      <h1 className="text-center font-light leading-[1.05]" style={{ fontFamily: '"Cormorant Garamond", serif', fontSize: "2.6rem", color: TOK.cream }}>
        Build Your Own<br />Installation
      </h1>
      <div className="mt-8 space-y-4 px-2 max-w-md mx-auto">
        <p className="italic leading-relaxed text-center" style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif', fontSize: "1.25rem" }}>
          Take a breath.
        </p>
        <p className="italic leading-relaxed text-center" style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif', fontSize: "1.05rem" }}>
          Choose your words, your voice, your music and your frequency.
          Shape each element with intention — and create something
          unmistakably yours.
        </p>
      </div>
      <div className="mt-10 mb-6 flex justify-center">
        <button
          onClick={onBegin}
          data-testid="journey-opening-begin"
          className="inline-flex items-center gap-3 px-10 py-4 rounded-full transition-all"
          style={{
            background: "linear-gradient(135deg, rgba(215,187,122,0.30) 0%, rgba(215,187,122,0.14) 100%)",
            border: `1px solid ${TOK.gold}`,
            color: TOK.cream,
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.78rem",
            letterSpacing: "0.28em",
            textTransform: "uppercase",
            boxShadow: "0 0 32px rgba(215,187,122,0.22)",
          }}
        >
          <Sparkles size={15} /> Begin My Installation
        </button>
      </div>
    </div>
  );
}

// ── Voice Level Card · Mar 3 2026 ────────────────────────────────
// Three-way toggle: Audible / Whisper / Subliminal. Subliminal
// routes this installation through the 6-hour Overnight renderer
// on the backend (any Signature can become an overnight variant).
function VoiceLevelCard({ level, onChange, durationMin }) {
  const options = [
    {
      id: "audible",
      label: "Audible",
      caption: "The classic Reclaim install — your voice up front.",
      chip: "Standard · 3-phase envelope",
    },
    {
      id: "whisper",
      label: "Whisper",
      caption: "Voice sits under the music. For a light background install.",
      chip: "−6 dB · Feels ambient",
    },
    {
      id: "subliminal",
      label: "Subliminal",
      caption: "Six hours of your voice at a whisper on a theta bed. Play it as you fall asleep.",
      chip: "−28 dB · Overnight · 6-hour loop",
    },
  ];
  return (
    <div
      className="mt-4 rounded-2xl p-4"
      style={{
        background: "rgba(245,236,217,0.03)",
        border: `1px solid ${TOK.goldMist}`,
      }}
      data-testid="voice-level-card"
    >
      <div className="flex items-center gap-2 mb-3">
        <div
          className="text-[0.6rem] uppercase tracking-[0.28em]"
          style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
        >
          Voice level
        </div>
        {level === "subliminal" && (
          <span
            className="text-[0.5rem] uppercase tracking-[0.22em] rounded-full px-2 py-[2px]"
            style={{
              background: "rgba(74,58,106,0.35)",
              border: "1px solid rgba(200,168,103,0.55)",
              color: TOK.cream,
              fontFamily: '"Manrope", sans-serif',
            }}
          >
            Overnight · 6 hrs
          </span>
        )}
      </div>
      <div role="radiogroup" aria-label="Voice level" className="flex flex-col gap-2">
        {options.map((opt) => {
          const active = level === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onChange(opt.id)}
              data-testid={`voice-level-${opt.id}`}
              className="text-left rounded-xl p-3 transition-all"
              style={{
                background: active ? "rgba(215,187,122,0.10)" : "rgba(245,236,217,0.02)",
                border: `1px solid ${active ? TOK.gold : TOK.goldMist}`,
                color: TOK.cream,
              }}
            >
              <div className="flex items-baseline justify-between gap-2">
                <div
                  className="font-light"
                  style={{ fontFamily: '"Cormorant Garamond", serif', fontSize: "1.05rem", color: TOK.cream }}
                >
                  {opt.label}
                </div>
                <div
                  className="text-[0.55rem] uppercase tracking-[0.22em] shrink-0"
                  style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
                >
                  {opt.chip}
                </div>
              </div>
              <div
                className="mt-1 text-[0.78rem] leading-relaxed italic"
                style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif' }}
              >
                {opt.caption}
              </div>
            </button>
          );
        })}
      </div>
      {level === "subliminal" && (
        <p
          className="mt-3 text-[0.72rem] leading-relaxed italic"
          style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif' }}
        >
          Your {durationMin || 20}-minute install becomes a seamless 6-hour overnight loop —
          your cloned voice at a whisper, on a 6 Hz theta bed, auto-faded so it never jolts
          you awake.
        </p>
      )}
    </div>
  );
}


// ── Voice Choice Card ───────────────────────────────────────────
// Lives inside the Atmosphere step. Positions "Record My Voice" as
// the recommended path; the actual MediaRecorder ships in the next
// release. For now this captures the preference + a poetic nudge.
function VoiceChoiceCard({ choice, onChange, gender, onGenderChange, hasClone, cloneResetsOn }) {
  return (
    <div className="mt-4 mb-5 p-5 rounded-xl" style={{ background: "rgba(215,187,122,0.05)", border: `1px solid ${TOK.goldMist}` }} data-testid="journey-voice-card">
      <p className="uppercase tracking-[0.28em] text-[0.55rem] mb-2" style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}>
        Your Voice Carries Unique Power
      </p>
      <p className="italic text-sm leading-relaxed mb-4" style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif' }}>
        We gently encourage you to record your own words. There is something profoundly powerful about hearing your own voice speak directly to your subconscious mind. Not someone else&apos;s truth. <span style={{ color: TOK.cream }}>Your truth.</span>
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <button
          onClick={() => onChange("own")}
          data-testid="journey-voice-own"
          className="text-left p-3 rounded-lg transition-all"
          style={{
            background: choice === "own" ? "rgba(215,187,122,0.22)" : "rgba(215,187,122,0.08)",
            border: `1px solid ${choice === "own" ? TOK.gold : TOK.goldMist}`,
            boxShadow: choice === "own" ? "0 0 18px rgba(215,187,122,0.18)" : "none",
          }}
        >
          <p className="uppercase tracking-[0.22em] text-[0.6rem]" style={{ color: TOK.gold, fontFamily: '"Manrope", sans-serif' }}>
            ★ Recommended
          </p>
          <p style={{ color: TOK.cream, fontFamily: '"Cormorant Garamond", serif', fontSize: "1.1rem" }}>Record My Voice</p>
          <p className="italic text-xs mt-0.5" style={{ color: TOK.creamDim }}>Speak each line slowly, with feeling.</p>
        </button>
        <button
          onClick={() => onChange("reclaim")}
          data-testid="journey-voice-reclaim"
          className="text-left p-3 rounded-lg transition-all"
          style={{
            background: choice === "reclaim" ? "rgba(215,187,122,0.16)" : "transparent",
            border: `1px solid ${choice === "reclaim" ? TOK.gold : TOK.goldMist}`,
          }}
        >
          <p className="uppercase tracking-[0.22em] text-[0.6rem]" style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}>
            Beautiful English voice
          </p>
          <p style={{ color: TOK.cream, fontFamily: '"Cormorant Garamond", serif', fontSize: "1.1rem" }}>Use Soulumo Voice</p>
          <p className="italic text-xs mt-0.5" style={{ color: TOK.creamDim }}>The voice we have curated, ready instantly.</p>
        </button>
      </div>
      {/* Male/Female sub-picker — appears when Reclaim voice is
          chosen. Female = Lily (backend default); Male = Michael's
          cloned voice. Feb 24 2026. */}
      {choice === "reclaim" && onGenderChange && (
        <div className="mt-4 pt-4" style={{ borderTop: `1px solid ${TOK.goldMist}` }} data-testid="journey-reclaim-voice-gender">
          <p className="uppercase tracking-[0.28em] text-[0.55rem] mb-2" style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}>
            Soulumo voice
          </p>
          <div className="flex gap-2 flex-wrap" role="radiogroup" aria-label="Soulumo voice gender">
            {hasClone && (
              <button
                type="button"
                role="radio"
                aria-checked={gender === "clone"}
                onClick={() => onGenderChange("clone")}
                data-testid="journey-reclaim-voice-clone"
                className="flex-1 px-4 py-2.5 rounded-full transition-all"
                style={{
                  background: gender === "clone" ? TOK.gold : "transparent",
                  color: gender === "clone" ? TOK.ink : TOK.cream,
                  border: `1px solid ${gender === "clone" ? TOK.gold : TOK.goldMist}`,
                  fontFamily: '"Manrope", sans-serif',
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  fontSize: "0.62rem",
                  fontWeight: 600,
                  minHeight: 44,
                }}
              >
                My voice · Clone
              </button>
            )}
            <button
              type="button"
              role="radio"
              aria-checked={gender === "female"}
              onClick={() => onGenderChange("female")}
              data-testid="journey-reclaim-voice-female"
              className="flex-1 px-4 py-2.5 rounded-full transition-all"
              style={{
                background: gender === "female" ? TOK.gold : "transparent",
                color: gender === "female" ? TOK.ink : TOK.cream,
                border: `1px solid ${gender === "female" ? TOK.gold : TOK.goldMist}`,
                fontFamily: '"Manrope", sans-serif',
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                fontSize: "0.62rem",
                fontWeight: 600,
                minHeight: 44,
              }}
            >
              Female · Lily
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={gender === "male"}
              onClick={() => onGenderChange("male")}
              data-testid="journey-reclaim-voice-male"
              className="flex-1 px-4 py-2.5 rounded-full transition-all"
              style={{
                background: gender === "male" ? TOK.gold : "transparent",
                color: gender === "male" ? TOK.ink : TOK.cream,
                border: `1px solid ${gender === "male" ? TOK.gold : TOK.goldMist}`,
                fontFamily: '"Manrope", sans-serif',
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                fontSize: "0.62rem",
                fontWeight: 600,
                minHeight: 44,
              }}
            >
              Male · Michael
            </button>
          </div>
          <p className="italic text-xs mt-2" style={{ color: TOK.creamMist, fontFamily: '"Cormorant Garamond", serif' }}>
            {gender === "clone" && cloneResetsOn
              ? `You've used this month's voice allowance, so this installation will use the Soulumo narrator. Your voice returns on ${new Date(`${cloneResetsOn}T00:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "long" })}.`
              : gender === "clone"
              ? "Your own cloned voice speaks every line you haven't recorded yourself."
              : gender === "male"
              ? "Michael's warm, grounded delivery — cloned from his own studio takes."
              : "Lily's soft British voice — Soulumo's default narrator."}
          </p>
        </div>
      )}
      {choice === "own" && (
        <p className="italic text-xs mt-3" style={{ color: TOK.goldSoft, fontFamily: '"Cormorant Garamond", serif' }}>
          ⌁ Voice recording is arriving in the next release. For this journey, the beautiful Soulumo voice will be used so you do not have to wait. Your preference is saved.
        </p>
      )}
    </div>
  );
}